import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  X,
  Play,
  Settings,
  Package,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Users,
  Hash,
  GripVertical,
  Clock,
  ClipboardList,
  Download,
  Printer,
  User,
  LogOut,
  Activity,
  AlertTriangle,
  Power,
  TrendingUp,
  TrendingDown,
  Gauge,
  PauseCircle,
  PlayCircle,
  Radio,
  LucideIcon,
  Sun,
  Moon,
  CheckCircle,
  AlertCircle,
  XCircle,
  PowerOff,
  Hand,
  AlertOctagon,
  Cable,
  Cylinder,
  Maximize2,
  History,
  X as XIcon
} from 'lucide-react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import apiService, { api } from '../services/api';
import StoppageReportExport from '../components/StoppageReportExport';


import toast from 'react-hot-toast';
import MachineLogs from '../components/MachineLogs';
import { useMachineData } from '../hooks/useMachineData';
import { useMachineStatusConfig } from '../hooks/useMachineStatusConfig';
import { ShiftService } from '../services/shiftService';
import ShiftAssignmentModal from '../components/ShiftAssignmentModal';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { isMachineViewer } from '../utils/homeRoute';
import { useSuperAdminCompany } from '../contexts/SuperAdminCompanyContext';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { MACHINE_CONSTANTS, STOP_REASONS } from '../constants/machineConstants';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Machine {
  id: number;
  number: string;
  tenantId?: number | null;
  machineType?: 'water_jacket' | 'rapier_jacket';
  groupId?: number;
  group?: {
    id: number;
    name: string;
    description?: string;
    shiftAEmployeeId?: number;
    shiftBEmployeeId?: number;
    shiftAEmployee?: {
      id: number;
      type: string;
      rate: number;
      shiftARate?: number;
      shiftBRate?: number;
      user: {
        id: number;
        name: string;
        email: string;
      };
    };
    shiftBEmployee?: {
      id: number;
      type: string;
      rate: number;
      shiftARate?: number;
      shiftBRate?: number;
      user: {
        id: number;
        name: string;
        email: string;
      };
    };
  };
  productions?: Array<{
    id: number;
    meters: number;
    metersProduced: number;
    status: string;
    beam?: {
      id: number;
      number: string;
      totalMeters: number;
    };
    quality?: {
      id: number;
      name: string;
    };
    employee?: {
      id: number;
      user: {
        id: number;
        name: string;
      };
    };
  }>;
  pickValue: number;
  isOn: boolean;
  efficiency: number;
  efficiencyThreshold?: number; // Per-machine efficiency threshold
  onlineHours?: number; // Total online hours from efficiency API
  offlineHours?: string; // Hours with no signal today, "Xh Ym" (status API)
  totalMeters?: number; // Total meters from efficiency API
  meters12HourDay?: number; // Meters for day shift (12PM-12AM)
  meters12HourNight?: number; // Meters for night shift (12AM-12PM)
  dayShiftEfficiency?: number; // Day shift efficiency (8 AM - 8 PM)
  nightShiftEfficiency?: number; // Night shift efficiency (8 PM - 8 AM)
  rpm?: number; // Live RPM from WebSocket
  reason?: string; // Machine state reason from WebSocket
  stopCount?: number; // Number of times machine stopped in last 24 hours
  currentStopStartedAt?: number | null; // Epoch ms when the current ongoing stop began (null if running)
  lastUpdate?: string | null; // Last update timestamp
  isStale?: boolean; // True if machine hasn't received data in past 1 minute
  createdAt: string;
  updatedAt: string;
  isHistorical?: boolean; // True when data is from a historical window (e.g., last day)
  pickPeriods?: Array<{
    pickValue: number;
    startTime: string;
    endTime: string;
    startTimeFormatted: string;
    endTimeFormatted: string;
    meters: number;
    onlineHours: number;
    avgRpm: number;
  }>;
  // Beam tracking fields
  beamNumber?: string;
  beamName?: string;
  beamTotalMeters?: number;
  beamShortagePercentage?: number;
  beamEffectiveMeters?: number;
  beamMetersUsed?: number;
  beamMetersRemaining?: number;
  beamDaysRemaining?: number;
  beamAttachedAt?: string;
}


interface MachineForm {
  from: number;
  to: number;
  machineType: 'water_jacket' | 'rapier_jacket';
  tenantId?: number | null;
}


interface Beam {
  id: number;
  number: string;
  totalMeters: number;
  metersUsed: number;
  metersRemaining: number;
  weight?: number;
  panno?: string;
  quality?: {
    id: number;
    name: string;
  };
  yarns?: Array<{
    id: number;
    name: string;
    isActive: boolean;
    BeamYarn: {
      meters: number;
    };
  }>;
}

interface Employee {
  id: number;
  user: {
    name: string;
  };
}

interface Quality {
  id: number;
  name: string;
  isActive: boolean;
}

interface ProductionForm {
  beamId: number;
  qualityId: number;
  employeeId: number;
  meters: number;
  pickValue: number;
  errorRate: number;
}

// ---- Status color helpers --------------------------------------------------
// The resolved status color (from machineStatusDefaults, DB-overridable) arrives
// as either `#rrggbb` or `rgb(r, g, b)`. These derive tints/shades from it so the
// whole card — top border, header tint, status pill, donut ring, numbers — reads
// as one color per state.
const statusToRgb = (color: string): [number, number, number] | null => {
  if (!color) return null;
  if (color.startsWith('rgb')) {
    const n = color.match(/\d+/g);
    if (n && n.length >= 3) return [Number(n[0]), Number(n[1]), Number(n[2])];
  }
  const h = color.match(/#([0-9a-fA-F]{6})/);
  if (h) {
    const v = parseInt(h[1], 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  }
  return null;
};
// Faint wash for header backgrounds.
const withAlpha = (color: string, alpha: number): string => {
  const rgb = statusToRgb(color);
  return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : color;
};
// Darken toward a readable shade (factor < 1). Used for pill backgrounds and the
// numbers printed on white, where a light status color would fail contrast.
const shade = (color: string, factor: number): string => {
  const rgb = statusToRgb(color);
  return rgb
    ? `rgb(${Math.round(rgb[0] * factor)}, ${Math.round(rgb[1] * factor)}, ${Math.round(rgb[2] * factor)})`
    : color;
};

// Humanize a stopped-for duration (ms) as a live-ticking value:
// "Xs" (<1m, ticks every second) / "Xm Ys" (<1h) / "Xh Ym" (<1d) / "Xd Yh".
const formatStoppedElapsed = (ms: number): string => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (totalMin < 60) return `${totalMin}m ${secs}s`;
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours < 24) return `${hours}h ${mins}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

// Red banner on a non-running card (shown when the superadmin highlight setting is on):
// a live "Stopped for Xh Ym" timer that ticks up, plus today's stop count.
const StoppedBanner: React.FC<{ startedAt?: number | null; stopCount?: number }> = ({ startedAt, stopCount }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    // Re-render every second so the elapsed timer ticks up live (1s, 2s, 3s…).
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = startedAt ? formatStoppedElapsed(Date.now() - startedAt) : null;
  return (
    <div
      className="flex items-center justify-between gap-1.5 text-[11px] font-bold uppercase text-white"
      style={{ padding: '4px 12px', background: '#DC2626' }}
    >
      {/* The live timer is the priority — never let it shrink/truncate. The stop
          count yields (truncates) on narrow tiles; the full count is also shown
          in the tile footer, so clipping it here loses nothing. */}
      <span className="inline-flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap">
        <Clock className="w-3 h-3 flex-shrink-0" />
        {elapsed ? `Stopped ${elapsed}` : 'Stopped'}
      </span>
      {(stopCount ?? 0) > 0 && (
        <span className="opacity-90 truncate min-w-0">{stopCount} {stopCount === 1 ? 'stop' : 'stops'}</span>
      )}
    </div>
  );
};

// Compact "stopped for" timer for the TV / kiosk card. Same live per-second tick
// as StoppedBanner, but sized with the tv-card tokens so it fits the dense grid.
const TvStoppedTimer: React.FC<{ startedAt?: number | null }> = ({ startedAt }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!startedAt) return null;
  return (
    <div className="tv-card__stopped">
      <Clock />
      <span>Stopped {formatStoppedElapsed(Date.now() - startedAt)}</span>
    </div>
  );
};

// ── Stopped-card blink rule ──
// A stopped card blinks only once its current stop has lasted this long.
// Short enough to flag a real stop almost immediately, long enough to skip the
// briefest weft/warp-break fixes (which clear in a few seconds). The live stop
// timer on the banner shows from 0s regardless; this only gates the blink.
const STOP_BLINK_AFTER_MS = 15 * 1000;

// Per-person "I have seen this stop" marks. When a user opens a machine's STOPS
// list, we remember the stop's start time (currentStopStartedAt) for that user and
// machine, so the card stops blinking for them. A new stop has a new start time,
// so it blinks again. Stored as { [machineId]: stopStartedAtMs } per user id.
// localStorage can throw (private mode, blocked storage), so every access is guarded.
const stopAckStorageKey = (userId: number | string) => `machineStopAck:v1:${userId}`;

const readStopAcks = (userId: number | string | null | undefined): Record<string, number> => {
  if (userId === null || userId === undefined) return {};
  try {
    const raw = window.localStorage.getItem(stopAckStorageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeStopAcks = (userId: number | string | null | undefined, acks: Record<string, number>) => {
  if (userId === null || userId === undefined) return;
  try {
    window.localStorage.setItem(stopAckStorageKey(userId), JSON.stringify(acks));
  } catch {
    // Storage unavailable: the mark still holds for this page session (React state).
  }
};

// True once the stop that began at `startedAt` has lasted STOP_BLINK_AFTER_MS.
// While waiting (`watch` on, not yet reached) it re-checks every second, so a card
// starts blinking right at the 15s mark without a page refresh. The interval is
// cleared the moment the threshold is reached, so it only ticks during the wait.
const useStoppedLongEnough = (startedAt: number | null | undefined, watch: boolean): boolean => {
  const [now, setNow] = useState(() => Date.now());
  const reached = !!startedAt && now - startedAt >= STOP_BLINK_AFTER_MS;
  useEffect(() => {
    if (!watch || !startedAt || reached) return;
    setNow(Date.now()); // `now` may be stale if this stop began after mount
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [watch, startedAt, reached]);
  return reached;
};

// A <div> that adds the stopped-blink class once its stop is 15+ min old. For
// places that render cards in a plain function (TV mode), where hooks can't run.
const StopBlinkDiv: React.FC<{
  candidate: boolean;
  startedAt?: number | null;
  className: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ candidate, startedAt, className, style, children }) => {
  const longEnough = useStoppedLongEnough(startedAt, candidate);
  return (
    <div className={`${className}${candidate && longEnough ? ' machine-card-stopped' : ''}`} style={style}>
      {children}
    </div>
  );
};

// Clickable status-chip categories -> the machines they count. Keys match the
// buckets built in `statusGroups` / `statusCounts`. `badge`/`title` are the per
// -category colors used in the "machines in this category" modal.
type StatusCategoryKey =
  | 'running' | 'lowEfficiency' | 'poorEfficiency' | 'problemEfficiency'
  | 'fault' | 'manual' | 'offline';

const STATUS_CATEGORY_META: Record<StatusCategoryKey, { label: string; badge: string; title: string }> = {
  running:           { label: 'Running',  badge: 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100',         title: 'text-green-700' },
  lowEfficiency:     { label: 'Low Eff',  badge: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300 hover:bg-fuchsia-100', title: 'text-fuchsia-700' },
  poorEfficiency:    { label: 'Poor Eff', badge: 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100',     title: 'text-yellow-700' },
  problemEfficiency: { label: 'Problem',  badge: 'bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100',     title: 'text-orange-700' },
  fault:             { label: 'Fault',    badge: 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100',                 title: 'text-red-700' },
  manual:            { label: 'Manual',   badge: 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100',             title: 'text-blue-700' },
  offline:           { label: 'Offline',  badge: 'bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200',       title: 'text-slate-700' },
};

const Machines: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedCompany } = useCompany();
  const { scope } = useSuperAdminCompany();
  const showCompanyColumn = user?.role === 'SUPERADMIN' && scope.mode === 'all';
  const [companyPickerTenantId, setCompanyPickerTenantId] = useState<number | null>(null);
  // A machine-viewer login must have a company chosen before we fetch/show machines,
  // otherwise the first request goes out unscoped and shows an empty grid.
  const needsCompany = isMachineViewer(user) && !selectedCompany;
  const selectedTenantId = selectedCompany?.tenantId ?? null;
  const { config, resolveStatus } = useMachineStatusConfig();
  // Superadmin "highlight stopped cards" toggle. Read here (inside MachineCard the
  // name `config` is shadowed by the per-card icon/color config).
  const highlightStoppedCardsEnabled = !!config.highlightStoppedCards;

  // Stops this user has already looked at (opened the STOPS list). See readStopAcks.
  const stopAckUserId = user?.id ?? null;
  const [stopAcks, setStopAcks] = useState<Record<string, number>>(() => readStopAcks(stopAckUserId));
  useEffect(() => {
    setStopAcks(readStopAcks(stopAckUserId));
  }, [stopAckUserId]);

  // The first time THIS client sees a machine stopped (manual/fault), remember the
  // instant. It's used as a fallback stop-start until the server's currentStopStartedAt
  // (derived from the time-series DB, which lags a few seconds) catches up — so the live
  // timer + blink appear consistently the moment a tile goes stopped, not a cycle later.
  const clientStopStartRef = useRef<Map<number, number>>(new Map());

  // Best-known stop-start epoch for a machine. Prefers the server's currentStopStartedAt
  // (accurate, and always <= the client time, so switching never jumps the timer back);
  // falls back to the client-observed time while that's still null; forgets when running.
  const getEffectiveStopStart = (machine: Machine, stopped: boolean): number | null => {
    const map = clientStopStartRef.current;
    if (!stopped) { map.delete(machine.id); return null; }
    if (machine.currentStopStartedAt != null) return machine.currentStopStartedAt;
    let t = map.get(machine.id);
    if (t == null) { t = Date.now(); map.set(machine.id, t); }
    return t;
  };

  // Called when the user opens a machine's STOPS list: stop blinking for this stop.
  const acknowledgeStop = (machine: Machine) => {
    const startedAt = machine.currentStopStartedAt ?? clientStopStartRef.current.get(machine.id) ?? null;
    if (!startedAt || stopAcks[machine.id] === startedAt) return;
    const next = { ...stopAcks, [machine.id]: startedAt };
    setStopAcks(next);
    writeStopAcks(stopAckUserId, next);
  };

  // Stopped-tile state for a machine given its live (WebSocket) on/off flag. `stopped` is
  // the manual/fault/stopped condition (highlight on, not running, not offline, not stale);
  // `effStart` is the stop-start to show/diff (server time or client fallback); `blinkCandidate`
  // gates the blink minus the STOP_BLINK_AFTER_MS wait, which useStoppedLongEnough applies.
  // Manual and fault stops both count.
  const stopStateFor = (machine: Machine, isOn: boolean): { stopped: boolean; effStart: number | null; blinkCandidate: boolean } => {
    const stopped =
      highlightStoppedCardsEnabled &&
      !isOn &&
      !machine.isHistorical &&
      !machine.isStale &&
      (machine.reason || '').toLowerCase() !== 'offline';
    const effStart = getEffectiveStopStart(machine, stopped);
    const blinkCandidate = stopped && effStart != null && stopAcks[machine.id] !== effStart;
    return { stopped, effStart, blinkCandidate };
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingMachine, setDeletingMachine] = useState<Machine | null>(null);
  const [groupByType] = useState(true);
  const [showGroupingModal, setShowGroupingModal] = useState(false);
  const [selectedMachines, setSelectedMachines] = useState<number[]>([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [selectedMachineForProduction, setSelectedMachineForProduction] = useState<Machine | null>(null);
  const [showMachineSettingsModal, setShowMachineSettingsModal] = useState(false);
  const [statusRange, setStatusRange] = useState<'today' | 'lastDay'>('today');
  const [mobileSelectedGroup, setMobileSelectedGroup] = useState<string | null>(null);

  // TV / kiosk mode — hides the app shell and stretches the grid for a 42" screen
  const [tvMode, setTvMode] = useState(false);
  const [tvPrevShift, setTvPrevShift] = useState(false);
  const enterTvMode = async () => {
    setTvMode(true);
    try { await document.documentElement.requestFullscreen(); } catch { /* user may decline */ }
  };
  const exitTvMode = async () => {
    setTvMode(false);
    setTvPrevShift(false);
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* noop */ }
  };
  useEffect(() => {
    if (tvMode) {
      document.body.classList.add('tv-mode');
    } else {
      document.body.classList.remove('tv-mode');
      setTvPrevShift(false);
    }
    if (!tvMode) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') exitTvMode(); };
    const onFsChange = () => { if (!document.fullscreenElement) setTvMode(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      document.body.classList.remove('tv-mode');
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, [tvMode]);

  // Dropdown state management - track per machine ID to prevent closing on data updates
  const [dropdownOpenMap, setDropdownOpenMap] = useState<Map<number, boolean>>(new Map());

  // Track previous machine states to preserve when reason is 'ignored'
  const previousMachineStatesRef = useRef<Map<number, Machine>>(new Map());

  // Get background color based on machine status
  const getBgColor = (machine: any) => {
    const machineWithStatus = getMachineWithStatus(machine);
    const efficiency = machine.calculatedEfficiency ?? machine.efficiency ?? 0;
    const reason = machineWithStatus.reason || 'ignored';
    const threshold = machine.efficiencyThreshold || 95;
    const resolved = resolveStatus(efficiency, machineWithStatus.isOn, machine.isStale, reason, threshold);
    return { color: resolved.color, text: resolved.label };
  };

  // Helper functions for dropdown state
  const setIsDropdownOpen = (machineId: number, isOpen: boolean) => {
    setDropdownOpenMap(prev => {
      const newMap = new Map(prev);
      if (isOpen) {
        newMap.set(machineId, true);
      } else {
        newMap.delete(machineId);
      }
      return newMap;
    });
  };

  const isDropdownOpen = (machineId: number) => {
    return dropdownOpenMap.get(machineId) || false;
  };

  // WebSocket data
  const { machineData, connectionInfo } = useMachineData();

  // Helper function to check if user has permission for a module
  const hasModulePermission = (moduleName: string): boolean => {
    if (!user) return false;
    if (user.role === 'SUPERADMIN') return true;

    // Check role permissions
    if (user.userRole?.permissions) {
      const hasPermission = user.userRole.permissions.some((p: any) =>
        p.module?.name === moduleName && p.canView
      );
      if (hasPermission) return true;
    }

    // Check direct permissions
    if (user.permissions) {
      return user.permissions.some((p: any) =>
        p.module?.name === moduleName && p.canView
      );
    }

    return false;
  };

  // Get machine state from WebSocket data
  const getMachineState = (machineId: number) => {
    const allMachines = machineData as any[];
    if (!allMachines || allMachines.length === 0) {
      return null;
    }

    // Match by machineId (database machine.id)
    const state = allMachines.find(m => m.machineId === machineId);
    return state || null;
  };

  // Get machine with merged WebSocket status
  const getMachineWithStatus = (machine: Machine) => {
    const wsState = getMachineState(machine.id);
    // Use WebSocket isOnline if available, otherwise use database isOn
    // WebSocket data takes priority for real-time status
    const isOn = wsState !== null && wsState !== undefined
      ? wsState.isOnline
      : machine.isOn;
    return {
      ...machine,
      isOn, // Use WebSocket isOnline instead of database isOn
      rpm: wsState?.RPM || 0,
      reason: wsState?.reason || 'ignored'
    };
  };
  const [selectedMachineForSettings, setSelectedMachineForSettings] = useState<Machine | null>(null);
  const [selectedMachineForPickEdit, setSelectedMachineForPickEdit] = useState<Machine | null>(null);
  const [selectedMachineForEfficiencyEdit, setSelectedMachineForEfficiencyEdit] = useState<Machine | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedMachineForLogs, setSelectedMachineForLogs] = useState<Machine | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);
  const [showMakeGroupModal, setShowMakeGroupModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedMachineForExport, setSelectedMachineForExport] = useState<Machine | null>(null);
  const [exportPreset, setExportPreset] = useState<'today' | 'yesterday' | 'last3days' | 'custom'>('today');
  // Single-machine export shift filter: 'both' (full range), 'day' (8 AM–7 PM) or 'night' (7 PM–8 AM).
  const [exportShift, setExportShift] = useState<'both' | 'day' | 'night'>('both');
  const [exportStartDate, setExportStartDate] = useState<string>('');
  const [exportStartTime, setExportStartTime] = useState<string>('00:00');
  const [exportEndDate, setExportEndDate] = useState<string>('');
  const [exportEndTime, setExportEndTime] = useState<string>('23:59');
  const [isExporting, setIsExporting] = useState(false);
  const [showExportAllModal, setShowExportAllModal] = useState(false);
  const [exportAllPreset, setExportAllPreset] = useState<'today' | 'yesterday' | 'custom'>('today');
  const [exportAllStartDate, setExportAllStartDate] = useState<string>('');
  const [exportAllStartTime, setExportAllStartTime] = useState<string>('00:00');
  const [exportAllEndDate, setExportAllEndDate] = useState<string>('');
  const [exportAllEndTime, setExportAllEndTime] = useState<string>('23:59');
  const [exportAllShift, setExportAllShift] = useState<'both' | 'day' | 'night'>('both');
  const [exportAllCustomStartTime, setExportAllCustomStartTime] = useState<string>('08:00');
  const [exportAllCustomEndTime, setExportAllCustomEndTime] = useState<string>('08:00');
  const [isExportingAll, setIsExportingAll] = useState(false);
  // Per-machine stoppage report export (button inside the Stop Events modal).
  // The all-machines variant is handled by the shared <StoppageReportExport /> component.
  const [isExportingStoppage, setIsExportingStoppage] = useState(false);
  const [selectedUngroupedMachines, setSelectedUngroupedMachines] = useState<number[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [rangeFrom, setRangeFrom] = useState<number>(1);
  const [rangeTo, setRangeTo] = useState<number>(1);
  const [useRangeSelection, setUseRangeSelection] = useState(false);
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [showShiftAssignmentModal, setShowShiftAssignmentModal] = useState(false);
  const [selectedGroupForShiftAssignment, setSelectedGroupForShiftAssignment] = useState<any>(null);
  const [showMeterEntryModal, setShowMeterEntryModal] = useState(false);
  const [selectedMachineForMeterEntry, setSelectedMachineForMeterEntry] = useState<Machine | null>(null);
  const [meterEntryForm, setMeterEntryForm] = useState({
    meters: '',
    shiftType: 'A' as 'A' | 'B',
    entryDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [showStopEventsModal, setShowStopEventsModal] = useState(false);
  const [selectedMachineForStopEvents, setSelectedMachineForStopEvents] = useState<Machine | null>(null);
  const [stopEvents, setStopEvents] = useState<any[]>([]);
  const [showMachineCardModal, setShowMachineCardModal] = useState(false);
  const [selectedMachineForCardModal, setSelectedMachineForCardModal] = useState<Machine | null>(null);
  // Which status chip (Running / Fault / Offline …) was clicked, to list its machines
  const [selectedStatusCategory, setSelectedStatusCategory] = useState<StatusCategoryKey | null>(null);
  const [stopEventsData, setStopEventsData] = useState<{
    from?: string;
    to?: string;
    stopCount?: number;
    totalStopTimeFormatted?: string;
  } | null>(null);
  const [loadingStopEventsMachineId, setLoadingStopEventsMachineId] = useState<number | null>(null);
  const [editingNoteIdx, setEditingNoteIdx] = useState<string | null>(null);
  const [noteForm, setNoteForm] = useState<{ reason: string; note: string }>({ reason: '', note: '' });
  const [savingNote, setSavingNote] = useState(false);
  // Beam tracking state
  const [showBeamModal, setShowBeamModal] = useState(false);
  const [selectedMachineForBeam, setSelectedMachineForBeam] = useState<Machine | null>(null);
  const [beamForm, setBeamForm] = useState({ beamNumber: '', beamName: '', beamTotalMeters: '', shortagePercentage: '' });
  const [isAttachingBeam, setIsAttachingBeam] = useState(false);
  const queryClient = useQueryClient();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // WebSocket real-time data will be wired after we fetch machines below

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MachineForm>();

  const {
    register: registerProduction,
    handleSubmit: handleSubmitProduction,
    reset: resetProduction,
    setValue: setProductionValue,
    formState: { errors: productionErrors },
    watch: watchProduction,
  } = useForm<ProductionForm>({
    defaultValues: {
      beamId: 0,
      qualityId: 0,
      employeeId: 0,
      meters: 0,
      pickValue: 0,
      errorRate: 5, // Default 5% error rate
    },
  });

  const {
    register: registerSettings,
    handleSubmit: handleSubmitSettings,
    reset: resetSettings,
    formState: { errors: settingsErrors },
  } = useForm<{ pickValue: number; efficiencyThreshold: number }>();

  // Fetch only water_jacket machines
  const { data: machinesResponse, isLoading, error } = useQuery({
    // Include the selected company so switching refetches with the new tenant scope.
    queryKey: ['machines', 'water_jacket', selectedTenantId],
    queryFn: () => apiService.get('/machines?machineType=water_jacket'),
    // Don't fetch for a machine-viewer until their company is resolved.
    enabled: !needsCompany,
  });

  const machines = machinesResponse?.machines || [];

  // Keep all groups collapsed by default for clean view, but preserve expanded state on refetch
  useEffect(() => {
    // Only reset expanded groups on initial load
    // Preserve expanded state when machines are refetched (e.g., after settings update)
    if (isInitialLoadRef.current && machinesResponse) {
      setExpandedGroups(new Set());
      isInitialLoadRef.current = false;
    }
  }, [machinesResponse]);

  // Load and save group order from localStorage
  useEffect(() => {
    const savedOrder = localStorage.getItem('machineGroupOrder');
    if (savedOrder) {
      try {
        setGroupOrder(JSON.parse(savedOrder));
      } catch (error) {
        console.error('Error parsing saved group order:', error);
      }
    }
  }, []);

  // Save group order to localStorage whenever it changes
  useEffect(() => {
    if (groupOrder.length > 0) {
      localStorage.setItem('machineGroupOrder', JSON.stringify(groupOrder));
    }
  }, [groupOrder]);

  // Note: Initial machine states are requested automatically by WebSocket context
  // No need to request them again here to prevent infinite loops

  // Helper function to split machines into batches of 10
  const splitIntoBatches = (items: Machine[], batchSize: number = 10): Machine[][] => {
    const batches: Machine[][] = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }
    return batches;
  };

  // Create batches of machines (10 per batch)
  const machineBatches = useMemo(() => {
    if (!machines || machines.length === 0) return [];
    return splitIntoBatches(machines, 10);
  }, [machines]);

  const statusPath = statusRange === 'lastDay' ? '/machine-logs/status/last-day?machineType=water_jacket' : '/machine-logs/status/all?machineType=water_jacket';

  // Create batch queries sequentially (up to 20 batches)
  // Each query is enabled only after the previous one succeeds
  const batch0Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 0, machineBatches[0]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[0]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 0 && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch1Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 1, machineBatches[1]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[1]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 1 && batch0Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch2Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 2, machineBatches[2]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[2]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 2 && batch1Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch3Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 3, machineBatches[3]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[3]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 3 && batch2Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch4Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 4, machineBatches[4]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[4]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 4 && batch3Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch5Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 5, machineBatches[5]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[5]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 5 && batch4Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch6Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 6, machineBatches[6]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[6]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 6 && batch5Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch7Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 7, machineBatches[7]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[7]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 7 && batch6Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch8Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 8, machineBatches[8]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[8]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 8 && batch7Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch9Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 9, machineBatches[9]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[9]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 9 && batch8Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch10Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 10, machineBatches[10]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[10]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 10 && batch9Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch11Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 11, machineBatches[11]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[11]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 11 && batch10Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch12Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 12, machineBatches[12]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[12]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 12 && batch11Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch13Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 13, machineBatches[13]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[13]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 13 && batch12Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch14Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 14, machineBatches[14]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[14]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 14 && batch13Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch15Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 15, machineBatches[15]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[15]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 15 && batch14Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch16Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 16, machineBatches[16]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[16]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 16 && batch15Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch17Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 17, machineBatches[17]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[17]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 17 && batch16Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch18Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 18, machineBatches[18]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[18]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 18 && batch17Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch19Query = useQuery({
    queryKey: ['machines-efficiency-batch', statusRange, 19, machineBatches[19]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}&machineIds=${machineBatches[19]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 19 && batch18Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  // Collect all batch queries (only include those that have batches)
  const batchQueries = [
    batch0Query,
    batch1Query,
    batch2Query,
    batch3Query,
    batch4Query,
    batch5Query,
    batch6Query,
    batch7Query,
    batch8Query,
    batch9Query,
    batch10Query,
    batch11Query,
    batch12Query,
    batch13Query,
    batch14Query,
    batch15Query,
    batch16Query,
    batch17Query,
    batch18Query,
    batch19Query,
  ].slice(0, machineBatches.length);

  // Merge all batch results into single array
  const machinesEfficiency = useMemo(() => {
    const allResults: any[] = [];
    batchQueries.forEach(query => {
      if (query.data?.data && Array.isArray(query.data.data)) {
        allResults.push(...query.data.data);
      }
    });
    return allResults;
  }, [batchQueries.map(q => q.data)]);

  // Track which machines are currently refreshing due to efficiency/status API calls (per-batch fetching)
  const fetchingMachineIds = useMemo(() => {
    const ids = new Set<number>();
    batchQueries.forEach((query, idx) => {
      if (!query.isFetching) return;
      const batch = machineBatches[idx] || [];
      batch.forEach(m => ids.add(m.id));
    });
    return ids;
    // batchQueries is a fixed-size array (<= 20). We key memoization off fetching flags + batches.
  }, [
    machineBatches,
    batchQueries.map(q => (q.isFetching ? 1 : 0)).join(','),
  ]);

  // Merge machines with efficiency data and WebSocket data
  const machinesWithData = useMemo(() => {
    if (!machines || machines.length === 0) return [];

    return machines.map((machine: Machine) => {
      const efficiencyData = machinesEfficiency.find((e: any) => e.id === machine.id);
      const wsState = getMachineState(machine.id);
      const isHistorical = !!efficiencyData?.isHistorical;

      // Use efficiency data from API if available, otherwise fallback to machine defaults
      const efficiency = efficiencyData?.efficiency !== undefined && efficiencyData.efficiency !== null
        ? efficiencyData.efficiency
        : (machine.efficiency || 0);

      const totalMeters = efficiencyData?.totalMeters !== undefined && efficiencyData.totalMeters !== null
        ? efficiencyData.totalMeters
        : (efficiencyData?.totalMetersProduced !== undefined ? efficiencyData.totalMetersProduced : (machine.totalMeters || 0));

      const meters12HourDay = efficiencyData?.meters12HourDay !== undefined && efficiencyData.meters12HourDay !== null
        ? efficiencyData.meters12HourDay
        : (machine.meters12HourDay || 0);

      const meters12HourNight = efficiencyData?.meters12HourNight !== undefined && efficiencyData.meters12HourNight !== null
        ? efficiencyData.meters12HourNight
        : (machine.meters12HourNight || 0);

      const onlineHours = efficiencyData?.onlineHours !== undefined && efficiencyData.onlineHours !== null
        ? efficiencyData.onlineHours
        : (machine.onlineHours || 0);

      const offlineHours: string | undefined = efficiencyData?.offlineHours ?? machine.offlineHours;

      const dayShiftEfficiency = efficiencyData?.dayShiftEfficiency !== undefined && efficiencyData.dayShiftEfficiency !== null
        ? efficiencyData.dayShiftEfficiency
        : (machine.dayShiftEfficiency || 0);

      const nightShiftEfficiency = efficiencyData?.nightShiftEfficiency !== undefined && efficiencyData.nightShiftEfficiency !== null
        ? efficiencyData.nightShiftEfficiency
        : (machine.nightShiftEfficiency || 0);

      // Efficiency Threshold: Use from API if available, otherwise from machine, default to 95
      const efficiencyThreshold = efficiencyData?.efficiency !== undefined && efficiencyData.efficiencyThreshold !== null
        ? efficiencyData.efficiencyThreshold
        : (machine.efficiencyThreshold ?? 95);

      // RPM:
      //  - For historical data (last 24h), use RPM from API (window average)
      //  - For live view, use RPM from WebSocket
      const rpm = isHistorical
        ? (efficiencyData?.rpm ?? machine.rpm ?? 0)
        : (wsState?.RPM || 0);

      // Check if machine is offline due to no update in past 2 minutes (ONLY IN FRONTEND)
      // Date.now() returns UTC milliseconds since epoch - this is correct for comparison
      const now = Date.now();
      // Check both WebSocket timestamp and API lastUpdate
      // Backend sends IST-converted timestamps as ISO strings
      // IMPORTANT: Backend's utcToISTString() adds 5.5 hours then calls .toISOString()
      // This creates a timestamp that's 5.5 hours ahead in UTC
      // To get the actual UTC time, we need to subtract 5.5 hours from the parsed timestamp
      let wsTimestamp = 0;
      if (wsState?.timestamp) {
        const parsed = dayjs.utc(wsState.timestamp);
        if (parsed.isValid()) {
          // Subtract IST offset (5.5 hours) to get back to original UTC time
          wsTimestamp = parsed.subtract(5.5, 'hour').valueOf();
        }
      }

      let apiLastUpdate = 0;
      if (efficiencyData?.lastUpdate) {
        const parsed = dayjs.utc(efficiencyData.lastUpdate);
        if (parsed.isValid()) {
          // Subtract IST offset (5.5 hours) to get back to original UTC time
          apiLastUpdate = parsed.subtract(5.5, 'hour').valueOf();
        }
      }

      const lastUpdateTime = Math.max(wsTimestamp, apiLastUpdate);

      // Choose which timestamp to display (prefer the freshest source)
      let lastUpdateDisplay: string | null = null;
      if (lastUpdateTime > 0) {
        const displaySource = wsTimestamp >= apiLastUpdate ? wsState?.timestamp : efficiencyData?.lastUpdate;
        if (displaySource) {
          const parsed = dayjs.utc(displaySource);
          if (parsed.isValid()) {
            lastUpdateDisplay = parsed.format("hh:mm:ss A");
          }
        }
      }

      // Machine is stale if no update received in the last 2 minutes
      // If no timestamp available, consider it stale
      const isStale = isHistorical
        ? false // don't treat historical snapshots as stale
        : (lastUpdateTime === 0 || (now - lastUpdateTime) > (60 * 1000 * 2)); // 20 minutes

      // Use WebSocket isOnline if available, otherwise use database isOn
      // WebSocket data takes priority for real-time status
      // But if timestamp is older than 2 minutes, mark as offline
      let isOn = wsState !== null && wsState !== undefined
        ? (isStale ? false : wsState.isOnline)
        : (isStale ? false : machine.isOn);

      // Get the reason from WebSocket
      const newReason = isStale ? 'Offline' : (wsState?.reason || 'ignored');

      // Check if reason is 'ignored' - if so, preserve previous state (don't update UI)
      const previousState = previousMachineStatesRef.current.get(machine.id);
      if (newReason === 'ignored' && previousState) {
        // Preserve previous state when reason is 'ignored' - don't update UI
        return {
          ...previousState,
          // Still update efficiency and meters from API (these are safe to update)
          efficiency,
          totalMeters,
          meters12HourDay,
          meters12HourNight,
          onlineHours,
          offlineHours,
          dayShiftEfficiency,
          nightShiftEfficiency,
          efficiencyThreshold,
          stopCount: efficiencyData?.stopCount || 0,
          currentStopStartedAt: efficiencyData?.currentStopStartedAt ?? null,
          pickPeriods: efficiencyData?.pickPeriods || previousState.pickPeriods,
          lastUpdate: lastUpdateDisplay ?? previousState.lastUpdate,
          // Keep previous isOn, rpm, reason, lastUpdate, isStale from previous state
          // Update beam fields - if machine.beamNumber is explicitly null (detached via UI), respect that
          beamNumber: machine.beamNumber === null ? null : (efficiencyData?.beamNumber ?? previousState.beamNumber),
          beamName: machine.beamNumber === null ? null : (efficiencyData?.beamName ?? previousState.beamName),
          beamEffectiveMeters: machine.beamNumber === null ? null : (efficiencyData?.beamEffectiveMeters ?? previousState.beamEffectiveMeters),
          beamMetersUsed: machine.beamNumber === null ? null : (efficiencyData?.beamMetersUsed ?? previousState.beamMetersUsed),
          beamAttachedAt: machine.beamNumber === null ? null : (efficiencyData?.beamAttachedAt ?? previousState.beamAttachedAt),
        };
      }

      // Create new state
      const updatedMachine: Machine = {
        ...machine,
        efficiency,
        totalMeters,
        meters12HourDay,
        meters12HourNight,
        onlineHours,
        offlineHours,
        dayShiftEfficiency,
        nightShiftEfficiency,
        efficiencyThreshold,
        rpm,
        isOn,
        reason: newReason,
        lastUpdate: lastUpdateDisplay,
        stopCount: efficiencyData?.stopCount || 0,
        currentStopStartedAt: efficiencyData?.currentStopStartedAt ?? null,
        pickPeriods: efficiencyData?.pickPeriods,
        isStale,
        isHistorical,
        // Beam tracking - if machine.beamNumber is explicitly null (detached via UI), respect that
        beamNumber: machine.beamNumber === null ? null : (efficiencyData?.beamNumber ?? machine.beamNumber),
        beamName: machine.beamNumber === null ? null : (efficiencyData?.beamName ?? machine.beamName),
        beamTotalMeters: machine.beamNumber === null ? null : (efficiencyData?.beamTotalMeters ?? machine.beamTotalMeters),
        beamShortagePercentage: machine.beamNumber === null ? null : (efficiencyData?.beamShortagePercentage ?? machine.beamShortagePercentage),
        beamEffectiveMeters: machine.beamNumber === null ? null : (efficiencyData?.beamEffectiveMeters ?? machine.beamEffectiveMeters),
        beamMetersUsed: machine.beamNumber === null ? null : (efficiencyData?.beamMetersUsed ?? machine.beamMetersUsed),
        beamAttachedAt: machine.beamNumber === null ? null : (efficiencyData?.beamAttachedAt ?? machine.beamAttachedAt),
      };

      // Store the updated state for future reference
      previousMachineStatesRef.current.set(machine.id, updatedMachine);

      return updatedMachine;
    });
  }, [machines, machinesEfficiency, machineData]);

  // Helper function to calculate group efficiency
  const calculateGroupEfficiency = (groupMachines: Machine[]) => {
    if (groupMachines.length === 0) return 0;

    const totalEfficiency = groupMachines.reduce((sum, machine) => sum + machine.efficiency, 0);

    // Returns the average rounded to the nearest whole integer
    return Math.round(totalEfficiency / groupMachines.length);
  };

  // Helper function to get machine range (from-to)
  const getMachineRange = (groupMachines: Machine[]) => {
    if (groupMachines.length === 0) return { from: 0, to: 0 };
    const numbers = groupMachines.map(m => parseInt(m.number)).sort((a, b) => a - b);
    return { from: numbers[0], to: numbers[numbers.length - 1] };
  };

  // Helper function to get problematic machines in a group
  const getProblematicMachines = (machines: Machine[]) => {
    return machines.filter(machine => {
      const machineWithStatus = getMachineWithStatus(machine);
      const threshold = machine.efficiencyThreshold || 95;
      return !machineWithStatus.isOn || machine.efficiency < threshold;
    });
  };

  // Helper function to check if machine should be highlighted
  const shouldHighlightMachine = (machine: Machine) => {
    const machineWithStatus = getMachineWithStatus(machine);
    const threshold = machine.efficiencyThreshold || 95; // Default to 95% if not set
    const isLowEfficiency = machine.efficiency < threshold;
    const isStopped = !machineWithStatus.isOn || machine.efficiency === 0;
    return isLowEfficiency || isStopped;
  };

  // Helper function to get machine status
  const getMachineStatus = (machine: Machine) => {
    const machineWithStatus = getMachineWithStatus(machine);
    const efficiency = machine.efficiency || 0;
    const reason = machine.reason || 'ignored';
    const threshold = machine.efficiencyThreshold || 95;
    const resolved = resolveStatus(efficiency, machineWithStatus.isOn, machine.isStale, reason, threshold);
    return { text: resolved.statusText, color: resolved.color };
  };

  // Fetch beams (only if user has permission)
  const { data: beamsResponse } = useQuery({
    queryKey: ['beams'],
    queryFn: async () => {
      try {
        return await apiService.get('/beams');
      } catch (error: any) {
        // Silently handle permission errors - don't throw for 403 errors
        if (error.response?.status === 403) {
          return { beams: [] };
        }
        // For other errors, log but don't break the page
        console.error('Error fetching beams:', error);
        return { beams: [] };
      }
    },
    enabled: hasModulePermission('beams'),
    retry: false,
  });

  const beams = (beamsResponse as any)?.beams || [];

  // Fetch employees (only if user has permission)
  const { data: employeesResponse } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      try {
        return await apiService.get('/employees');
      } catch (error: any) {
        // Silently handle permission errors - don't throw for 403 errors
        if (error.response?.status === 403) {
          return { employees: [] };
        }
        // For other errors, log but don't break the page
        console.error('Error fetching employees:', error);
        return { employees: [] };
      }
    },
    enabled: hasModulePermission('employees'),
    retry: false,
  });

  const employees = (employeesResponse as any)?.employees || [];

  // Fetch qualities (only if user has permission)
  const { data: qualitiesResponse } = useQuery({
    queryKey: ['qualities'],
    queryFn: async () => {
      try {
        return await apiService.get('/qualities');
      } catch (error: any) {
        // Silently handle permission errors - don't throw for 403 errors
        if (error.response?.status === 403) {
          return { qualities: [] };
        }
        // For other errors, log but don't break the page
        console.error('Error fetching qualities:', error);
        return { qualities: [] };
      }
    },
    enabled: hasModulePermission('qualities'),
    retry: false,
  });

  const qualities = ((qualitiesResponse as any)?.qualities || []).filter((q: Quality) => q.isActive);


  // Create machine mutation
  const createMutation = useMutation({
    mutationFn: (data: MachineForm) => apiService.post('/machines', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      toast.success('Machine created successfully');
      setIsModalOpen(false);
      setCompanyPickerTenantId(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create machine');
    },
  });

  const createBulkMachinesMutation = useMutation({
    mutationFn: (data: MachineForm) => apiService.post('/machines/bulk', data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      const count = response.machines?.length || 0;
      toast.success(`${count} machines created successfully`);
      setIsModalOpen(false);
      setCompanyPickerTenantId(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create machines');
    },
  });


  // Delete machine mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/machines/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      toast.success('Machine deleted successfully');
      setDeletingMachine(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete machine');
    },
  });


  // Remove machines from group mutation
  const removeFromGroupMutation = useMutation({
    mutationFn: (machineIds: number[]) =>
      apiService.post('/machine-groups/remove', { machineIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      queryClient.invalidateQueries({ queryKey: ['machine-groups'] });
      toast.success('Machines removed from group successfully');
      setSelectedMachines([]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove machines from group');
    },
  });

  // Create group and assign machines mutation
  const createGroupAndAssignMutation = useMutation({
    mutationFn: async ({ groupName, groupDescription, machineIds }: { groupName: string; groupDescription?: string; machineIds: number[] }) => {
      // Convert machine IDs to machine numbers
      const selectedMachinesData = machines.filter((m: Machine) => machineIds.includes(m.id));
      const machineNumbers = selectedMachinesData.map((m: Machine) => parseInt(m.number));

      console.log('Selected machine IDs:', machineIds);
      console.log('Selected machine numbers:', machineNumbers);

      // Create the group with machine numbers
      const groupResponse = await apiService.post('/machine-groups', {
        name: groupName,
        description: groupDescription || '',
        machineNumbers: machineNumbers
      });

      return { groupId: groupResponse.group.id, groupName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      queryClient.invalidateQueries({ queryKey: ['machine-groups'] });
      toast.success(`Created group "${data.groupName}" and assigned ${selectedMachines.length} machine(s)`);
      setShowGroupingModal(false);
      setShowMakeGroupModal(false);
      setSelectedMachines([]);
      setSelectedUngroupedMachines([]);
      setNewGroupName('');
      setGroupName('');
      setGroupDescription('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create group and assign machines');
    },
  });

  // Start production mutation
  const startProductionMutation = useMutation({
    mutationFn: ({ machineId, data }: { machineId: number; data: ProductionForm }) =>
      apiService.post(`/production/start/${machineId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      queryClient.invalidateQueries({ queryKey: ['beams'] });
      toast.success('Production started successfully');
      setShowProductionModal(false);
      setSelectedMachineForProduction(null);
      resetProduction();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to start production');
    },
  });

  // Update machine settings mutation
  const updateMachineSettingsMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { pickValue: number; efficiencyThreshold: number } }) =>
      apiService.put(`/machines/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      toast.success('Machine settings updated successfully');

      // Close the appropriate modal based on which one is open
      if (selectedMachineForSettings && selectedMachineForSettings.id === variables.id) {
        setShowMachineSettingsModal(false);
        setSelectedMachineForSettings(null);
      }
      if (selectedMachineForPickEdit && selectedMachineForPickEdit.id === variables.id) {
        setSelectedMachineForPickEdit(null);
      }
      if (selectedMachineForEfficiencyEdit && selectedMachineForEfficiencyEdit.id === variables.id) {
        setSelectedMachineForEfficiencyEdit(null);
      }
      resetSettings();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update machine settings');
    },
  });

  // Helper to update a single machine's beam fields in the machines cache
  const updateMachineBeamCache = (machineId: number, beamFields: Partial<Machine>) => {
    // Update both machine type caches
    ['water_jacket', 'rapier_jacket'].forEach(type => {
      queryClient.setQueryData(['machines', type], (old: any) => {
        if (!old?.machines) return old;
        return {
          ...old,
          machines: old.machines.map((m: any) =>
            m.id === machineId ? { ...m, ...beamFields } : m
          ),
        };
      });
    });
    // Also update the in-memory previous state ref so merged data picks it up
    const prev = previousMachineStatesRef.current.get(machineId);
    if (prev) {
      previousMachineStatesRef.current.set(machineId, { ...prev, ...beamFields });
    }
  };

  // Beam mutations
  const attachBeamMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiService.post(`/machines/${id}/beam`, data),
    onSuccess: (_, variables) => {
      const { id, data } = variables;
      const effectiveMeters = data.beamTotalMeters * (1 - (data.shortagePercentage || 0) / 100);
      updateMachineBeamCache(id, {
        beamNumber: data.beamNumber,
        beamName: data.beamName || undefined,
        beamTotalMeters: data.beamTotalMeters,
        beamShortagePercentage: data.shortagePercentage || 0,
        beamEffectiveMeters: Math.round(effectiveMeters * 100) / 100,
        beamMetersUsed: 0,
        beamAttachedAt: new Date().toISOString(),
      });
      toast.success('Beam attached successfully');
      setShowBeamModal(false);
      setSelectedMachineForBeam(null);
      setBeamForm({ beamNumber: '', beamName: '', beamTotalMeters: '', shortagePercentage: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to attach beam');
    },
  });

  const detachBeamMutation = useMutation({
    mutationFn: (id: number) =>
      apiService.post(`/machines/${id}/beam/detach`, {}),
    onSuccess: (_, machineId) => {
      updateMachineBeamCache(machineId, {
        beamNumber: null as any,
        beamName: null as any,
        beamTotalMeters: null as any,
        beamShortagePercentage: null as any,
        beamEffectiveMeters: null as any,
        beamMetersUsed: null as any,
        beamAttachedAt: null as any,
      });
      toast.success('Beam detached successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to detach beam');
    },
  });

  const handleAttachBeam = () => {
    if (!selectedMachineForBeam) return;
    if (!beamForm.beamNumber || !beamForm.beamTotalMeters) {
      toast.error('Beam number and total meters are required');
      return;
    }
    attachBeamMutation.mutate({
      id: selectedMachineForBeam.id,
      data: {
        beamNumber: beamForm.beamNumber,
        beamName: beamForm.beamName || undefined,
        beamTotalMeters: parseFloat(beamForm.beamTotalMeters),
        shortagePercentage: parseFloat(beamForm.shortagePercentage) || 0,
      },
    });
  };

  const handleDetachBeam = (machine: Machine) => {
    if (window.confirm(`Detach beam ${machine.beamNumber} from Machine #${machine.number}?`)) {
      detachBeamMutation.mutate(machine.id);
    }
  };

  const onSubmit = (data: MachineForm) => {
    // For creating, validate range and create multiple machines
    if (data.from > data.to) {
      alert(t('machines.invalidRange'));
      return;
    }
    if (showCompanyColumn && !companyPickerTenantId) {
      toast.error('Please select a company');
      return;
    }
    createBulkMachinesMutation.mutate({ ...data, tenantId: showCompanyColumn ? companyPickerTenantId : undefined });
  };


  const handleDelete = (machine: Machine) => {
    setDeletingMachine(machine);
  };

  const confirmDelete = () => {
    if (deletingMachine) {
      deleteMutation.mutate(deletingMachine.id);
    }
  };

  const openModal = () => {
    reset({ from: 1, to: 1 });
    setCompanyPickerTenantId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCompanyPickerTenantId(null);
    reset();
  };

  const handleMachineSelect = (machineId: number) => {
    setSelectedMachines(prev =>
      prev.includes(machineId)
        ? prev.filter(id => id !== machineId)
        : [...prev, machineId]
    );
  };

  const toggleGroupExpansion = (groupName: string) => {
    // Store current scroll position before state update
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;

    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });

    // Restore scroll position after state update
    // Use setTimeout to ensure React has finished re-rendering
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto' // Instant scroll, no animation
      });
    }, 0);
  };

  const handleSelectAll = () => {
    if (selectedMachines.length === machines.length) {
      setSelectedMachines([]);
    } else {
      setSelectedMachines(machines.map((m: Machine) => m.id));
    }
  };

  const openGroupingModal = () => {
    if (selectedMachines.length === 0) {
      toast.error('Please select machines to group');
      return;
    }
    setNewGroupName('');
    setShowGroupingModal(true);
  };

  const closeGroupingModal = () => {
    setShowGroupingModal(false);
    setSelectedMachines([]);
    setNewGroupName('');
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    console.log(newGroupName.trim(), selectedMachines);
    createGroupAndAssignMutation.mutate({
      groupName: newGroupName.trim(),
      machineIds: selectedMachines
    });
  };

  const handleRemoveFromGroup = () => {
    removeFromGroupMutation.mutate(selectedMachines);
  };

  const handleStartProduction = (machine: Machine) => {
    setSelectedMachineForProduction(machine);

    // Auto-select shift employee if available
    let autoSelectedEmployeeId = 0;
    if (machine.group) {
      const currentShift = ShiftService.getCurrentShift();
      const shiftEmployee = ShiftService.getActiveShiftEmployee(machine.group, currentShift.shift);
      if (shiftEmployee) {
        autoSelectedEmployeeId = shiftEmployee.id;
      }
    }

    resetProduction({
      beamId: 0,
      qualityId: 0,
      employeeId: autoSelectedEmployeeId,
      meters: 0,
      pickValue: machine.pickValue,
      errorRate: 5, // Default 5% error rate
    });
    setShowProductionModal(true);
  };

  // Auto-populate meters when beam is selected
  const handleBeamChange = (beamId: number) => {
    if (beamId > 0) {
      const selectedBeam = beams.find((b: any) => b.id === beamId);
      if (selectedBeam) {
        setProductionValue('meters', selectedBeam.metersRemaining || 0);
      }
    } else {
      setProductionValue('meters', 0);
    }
  };

  const handleMachineSettings = (machine: Machine) => {
    setSelectedMachineForSettings(machine);
    resetSettings({
      pickValue: machine.pickValue,
      efficiencyThreshold: machine.efficiencyThreshold || 95
    });
    setShowMachineSettingsModal(true);
  };

  const handleViewLogs = (machine: Machine) => {
    setSelectedMachineForLogs(machine);
    setShowLogsModal(true);
  };

  const handlePickEdit = (machine: Machine) => {
    setSelectedMachineForPickEdit(machine);
    resetSettings({
      pickValue: machine.pickValue,
      efficiencyThreshold: machine.efficiencyThreshold || 95
    });
  };

  const handleEfficiencyEdit = (machine: Machine) => {
    setSelectedMachineForEfficiencyEdit(machine);
    resetSettings({
      pickValue: machine.pickValue,
      efficiencyThreshold: machine.efficiencyThreshold || 95
    });
  };

  const handleExportReport = (machine: Machine) => {
    setSelectedMachineForExport(machine);
    // Reset to default (today)
    setExportPreset('today');
    setExportShift('both');
    setExportStartDate('');
    setExportStartTime('00:00');
    setExportEndDate('');
    setExportEndTime('23:59');
    setShowExportModal(true);
  };

  // Decode a base64 payload from the backend into a downloaded file.
  const downloadBase64File = (base64: string, mime: string, filename: string) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  const deliverStoppageFile = (data: any, format: 'xlsx' | 'pdf', fallback: string) => {
    if (format === 'pdf') {
      downloadBase64File(data.pdf, 'application/pdf', data.filename || `${fallback}.pdf`);
    } else {
      downloadBase64File(data.excel, XLSX_MIME, data.filename || `${fallback}.xlsx`);
    }
  };

  // Per-machine stoppage report, from the Stop Events modal. Uses the modal's current
  // range (today / last 24h -> yesterday preset).
  const handleExportMachineStoppage = async (format: 'xlsx' | 'pdf') => {
    if (!selectedMachineForStopEvents) return;
    setIsExportingStoppage(true);
    try {
      const preset = statusRange === 'lastDay' ? 'yesterday' : 'today';
      const resp = await api.get(
        `/machine-logs/${selectedMachineForStopEvents.id}/stoppage-report?format=${format}&preset=${preset}`
      );
      deliverStoppageFile(resp.data, format, `machine-${selectedMachineForStopEvents.number}-stoppage`);
      toast.success('Stoppage report downloaded');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to export stoppage report');
    } finally {
      setIsExportingStoppage(false);
    }
  };

  const handleExportPDF = async (action: 'download' | 'print' = 'download') => {
    if (!selectedMachineForExport) return;

    // Validate custom date range if custom is selected
    if (exportPreset === 'custom') {
      if (!exportStartDate || !exportEndDate || !exportStartTime || !exportEndTime) {
        toast.error('Please select start and end dates with times');
        return;
      }

      try {
        const start = dayjs.tz(`${exportStartDate} ${exportStartTime}`, 'YYYY-MM-DD HH:mm', 'Asia/Kolkata');
        const end = dayjs.tz(`${exportEndDate} ${exportEndTime}`, 'YYYY-MM-DD HH:mm', 'Asia/Kolkata');

        if (!start.isValid() || !end.isValid()) {
          toast.error('Invalid date or time format');
          return;
        }

        if (end.isBefore(start)) {
          toast.error('End date/time must be after start date/time');
          return;
        }

        const diffMonths = end.diff(start, 'month', true);
        if (diffMonths > 2) {
          toast.error('Date range cannot exceed 2 months');
          return;
        }
      } catch (error) {
        console.error('Date validation error:', error);
        toast.error('Invalid date or time format');
        return;
      }
    }

    setIsExporting(true);
    try {
      const params = new URLSearchParams();

      // Send preset type or custom dates
      if (exportPreset !== 'custom') {
        params.append('preset', exportPreset);
      } else {
        // Format dates in IST timezone as ISO strings
        if (exportStartDate && exportStartTime) {
          try {
            const startDateTime = dayjs.tz(`${exportStartDate} ${exportStartTime}`, 'YYYY-MM-DD HH:mm', 'Asia/Kolkata');
            if (!startDateTime.isValid()) {
              throw new Error('Invalid start date/time');
            }
            params.append('startDate', startDateTime.toISOString());
          } catch (error) {
            console.error('Start date parsing error:', error);
            toast.error('Invalid start date or time format');
            setIsExporting(false);
            return;
          }
        }

        if (exportEndDate && exportEndTime) {
          try {
            const endDateTime = dayjs.tz(`${exportEndDate} ${exportEndTime}`, 'YYYY-MM-DD HH:mm', 'Asia/Kolkata');
            if (!endDateTime.isValid()) {
              throw new Error('Invalid end date/time');
            }
            params.append('endDate', endDateTime.toISOString());
          } catch (error) {
            console.error('End date parsing error:', error);
            toast.error('Invalid end date or time format');
            setIsExporting(false);
            return;
          }
        }
      }

      // Shift filter (day / night). 'both' is the default full-range report, so omit it.
      if (exportShift !== 'both') {
        params.append('shift', exportShift);
      }

      // Get base64 PDF from backend
      const response = await api.get(
        `/machine-logs/${selectedMachineForExport.id}/export-pdf?${params.toString()}`
      );

      // Decode base64 to binary
      const binaryString = atob(response.data.pdf);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (action === 'print') {
        const printWindow = window.open(url, '_blank');
        if (!printWindow) {
          toast.error('Please allow popups to print the report');
          window.URL.revokeObjectURL(url);
        } else {
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
          toast.success('PDF ready — print dialog opened');
        }
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `machine-${selectedMachineForExport.number}-report.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF report generated successfully');
      }

      setShowExportModal(false);
      setSelectedMachineForExport(null);
    } catch (error: any) {
      console.error('Export PDF error:', error);
      toast.error(error.response?.data?.message || 'Failed to generate PDF report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllMachinesPDF = async (action: 'download' | 'print' = 'download') => {
    // Validate custom date range if custom is selected
    if (exportAllPreset === 'custom') {
      if (!exportAllStartDate || !exportAllEndDate) {
        toast.error('Please select start and end dates');
        return;
      }

      try {
        const start = dayjs.tz(exportAllStartDate, 'YYYY-MM-DD', 'Asia/Kolkata');
        const end = dayjs.tz(exportAllEndDate, 'YYYY-MM-DD', 'Asia/Kolkata');

        if (!start.isValid() || !end.isValid()) {
          toast.error('Invalid date format');
          return;
        }

        if (end.isBefore(start)) {
          toast.error('End date must be after start date');
          return;
        }

        const diffMonths = end.diff(start, 'month', true);
        if (diffMonths > 2) {
          toast.error('Date range cannot exceed 2 months');
          return;
        }
      } catch (error) {
        console.error('Date validation error:', error);
        toast.error('Invalid date format');
        return;
      }
    }

    setIsExportingAll(true);
    try {
      const params = new URLSearchParams();

      // Send preset type
      if (exportAllPreset !== 'custom') {
        params.append('preset', exportAllPreset);

        // Add shift for yesterday
        if (exportAllPreset === 'yesterday') {
          params.append('shift', exportAllShift);
        }
      } else {
        // Custom range follows the mill day (8 AM to 8 AM IST): from the start date
        // 8 AM to the day after the end date 8 AM, capped at now.
        const startDateTime = dayjs.tz(exportAllStartDate, 'YYYY-MM-DD', 'Asia/Kolkata').add(8, 'hour');
        const endBoundary = dayjs.tz(exportAllEndDate, 'YYYY-MM-DD', 'Asia/Kolkata').add(1, 'day').add(8, 'hour');
        if (!startDateTime.isValid() || !endBoundary.isValid()) {
          toast.error('Invalid date format');
          setIsExportingAll(false);
          return;
        }
        const nowMs = Date.now();
        const endDateTime = endBoundary.valueOf() > nowMs ? dayjs(nowMs) : endBoundary;
        if (startDateTime.valueOf() >= endDateTime.valueOf()) {
          toast.error('No data yet: the start date begins at 8 AM IST');
          setIsExportingAll(false);
          return;
        }
        params.append('startDate', startDateTime.toISOString());
        params.append('endDate', endDateTime.toISOString());

        // Day or Night shift only, on each day of the range. Full day sends no shift.
        if (exportAllShift !== 'both') {
          params.append('shift', exportAllShift);
        }
      }

      // Get base64 PDF from backend
      params.append('machineType', 'water_jacket');
      const response = await api.get(
        `/machine-logs/export-all-pdf?${params.toString()}`
      );

      // Decode base64 to binary
      const binaryString = atob(response.data.pdf);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (action === 'print') {
        const printWindow = window.open(url, '_blank');
        if (!printWindow) {
          toast.error('Please allow popups to print the report');
          window.URL.revokeObjectURL(url);
        } else {
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
          toast.success('PDF ready — print dialog opened');
        }
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `all-machines-report.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF report generated successfully');
      }

      setShowExportAllModal(false);
    } catch (error: any) {
      console.error('Export PDF error:', error);
      toast.error(error.response?.data?.message || 'Failed to generate PDF report');
    } finally {
      setIsExportingAll(false);
    }
  };

  const handleAddMeterEntry = (machine: Machine) => {
    // Check if machine has active production
    if (!machine.productions || machine.productions.length === 0) {
      toast.error('No active production found for this machine. Please start production first.');
      return;
    }
    setSelectedMachineForMeterEntry(machine);
    // Reset form
    setMeterEntryForm({
      meters: '',
      shiftType: 'A',
      entryDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowMeterEntryModal(true);
  };

  // Add meter entry mutation
  const addMeterEntryMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiService.post('/meter-entries', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines', 'water_jacket'] });
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      setShowMeterEntryModal(false);
      setSelectedMachineForMeterEntry(null);
      toast.success('Meter entry added successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add meter entry');
    }
  });

  const handleSubmitMeterEntry = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMachineForMeterEntry || !selectedMachineForMeterEntry.productions || selectedMachineForMeterEntry.productions.length === 0) {
      toast.error('No active production found');
      return;
    }

    if (!meterEntryForm.meters || parseFloat(meterEntryForm.meters) <= 0) {
      toast.error('Please enter valid meters');
      return;
    }

    const production = selectedMachineForMeterEntry.productions[0];
    const meterData = {
      productionId: production.id,
      machineId: selectedMachineForMeterEntry.id,
      employeeId: production.employee?.id || 0,
      meters: parseFloat(meterEntryForm.meters),
      shiftType: meterEntryForm.shiftType,
      entryDate: meterEntryForm.entryDate,
      notes: meterEntryForm.notes || undefined
    };

    addMeterEntryMutation.mutate(meterData);
  };

  // New grouping functionality handlers
  const openMakeGroupModal = () => {
    setSelectedUngroupedMachines([]);
    setGroupName('');
    setGroupDescription('');
    setRangeFrom(1);
    setRangeTo(1);
    setUseRangeSelection(false);
    setShowMakeGroupModal(true);
  };

  const closeMakeGroupModal = () => {
    setShowMakeGroupModal(false);
    setSelectedUngroupedMachines([]);
    setGroupName('');
    setGroupDescription('');
    setRangeFrom(1);
    setRangeTo(1);
    setUseRangeSelection(false);
  };

  const handleUngroupedMachineSelect = (machineId: number) => {
    setSelectedUngroupedMachines(prev =>
      prev.includes(machineId)
        ? prev.filter(id => id !== machineId)
        : [...prev, machineId]
    );
  };

  const handleSelectAllUngrouped = () => {
    if (selectedUngroupedMachines.length === ungroupedMachines.length) {
      setSelectedUngroupedMachines([]);
    } else {
      setSelectedUngroupedMachines(ungroupedMachines.map((m: Machine) => m.id));
    }
  };

  const handleRangeSelection = () => {
    if (rangeFrom > rangeTo) {
      toast.error('Range "From" must be less than or equal to "To"');
      return;
    }

    const machinesInRange = ungroupedMachines.filter((machine: Machine) => {
      const machineNumber = parseInt(machine.number);
      return machineNumber >= rangeFrom && machineNumber <= rangeTo;
    });

    if (machinesInRange.length === 0) {
      toast.error(`No ungrouped machines found in range ${rangeFrom}-${rangeTo}`);
      return;
    }

    setSelectedUngroupedMachines(machinesInRange.map((m: Machine) => m.id));
    toast.success(`Selected ${machinesInRange.length} machines in range ${rangeFrom}-${rangeTo}`);
  };

  const handleCreateGroupFromUngrouped = () => {
    if (!groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    if (selectedUngroupedMachines.length === 0) {
      toast.error('Please select machines to group');
      return;
    }

    createGroupAndAssignMutation.mutate({
      groupName: groupName.trim(),
      groupDescription: groupDescription.trim(),
      machineIds: selectedUngroupedMachines
    });
  };

  // Handle drag and drop for group reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setGroupOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over?.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const onSubmitProduction = (data: ProductionForm) => {
    if (selectedMachineForProduction) {
      startProductionMutation.mutate({
        machineId: selectedMachineForProduction.id,
        data
      });
    }
  };

  const onSubmitPickEdit = (data: { pickValue: number }) => {
    if (!selectedMachineForPickEdit) return;

    if (window.confirm(`Are you sure you want to change pick value from ${selectedMachineForPickEdit.pickValue} to ${data.pickValue}?`)) {
      updateMachineSettingsMutation.mutate({
        id: selectedMachineForPickEdit.id,
        data: { pickValue: data.pickValue, efficiencyThreshold: selectedMachineForPickEdit.efficiencyThreshold || 95 }
      }, {
        onSuccess: () => {
          setSelectedMachineForPickEdit(null);
          resetSettings();
        }
      });
    }
  };

  const onSubmitEfficiencyEdit = (data: { efficiencyThreshold: number }) => {
    if (!selectedMachineForEfficiencyEdit) return;

    if (window.confirm(`Are you sure you want to change efficiency threshold to ${data.efficiencyThreshold}%?`)) {
      updateMachineSettingsMutation.mutate({
        id: selectedMachineForEfficiencyEdit.id,
        data: { pickValue: selectedMachineForEfficiencyEdit.pickValue, efficiencyThreshold: data.efficiencyThreshold }
      }, {
        onSuccess: () => {
          setSelectedMachineForEfficiencyEdit(null);
          resetSettings();
        }
      });
    }
  };

  const onSubmitSettings = (data: { pickValue: number; efficiencyThreshold: number }) => {
    if (selectedMachineForSettings) {
      updateMachineSettingsMutation.mutate({
        id: selectedMachineForSettings.id,
        data
      });
    }
  };

  const closeProductionModal = () => {
    setShowProductionModal(false);
    setSelectedMachineForProduction(null);
    resetProduction();
  };

  const closeSettingsModal = () => {
    setShowMachineSettingsModal(false);
    setSelectedMachineForSettings(null);
    resetSettings();
  };

  // Group machines by group (show both grouped and ungrouped machines)
  const groupedMachines = machinesWithData.reduce((acc: Record<string, Machine[]>, machine: Machine) => {
    const groupName = machine.group?.name || 'Ungrouped Machines';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(machine);
    return acc;
  }, {} as Record<string, Machine[]>);

  // Machines are not sorted by efficiency - kept in original order

  // Helper function to calculate average efficiency for a group
  const getGroupAverageEfficiency = (machines: Machine[]) => {
    if (machines.length === 0) return 0;
    const sum = machines.reduce((acc, machine) => acc + machine.efficiency, 0);
    return sum / machines.length;
  };

  // Separate grouped and ungrouped machines, apply custom ordering
  const groupedOnly = (Object.entries(groupedMachines) as [string, Machine[]][])
    .filter(([groupName]) => groupName !== 'Ungrouped Machines')
    .sort(([a, aMachines], [b, bMachines]) => {
      // If we have a saved order, use it
      if (groupOrder.length > 0) {
        const aIndex = groupOrder.indexOf(a);
        const bIndex = groupOrder.indexOf(b);

        // If both groups are in the saved order, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only one is in the saved order, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
      }

      // Sort by line number (not by efficiency)
      const aLineNum = parseInt(a.replace(/[^\d]/g, '')) || 0;
      const bLineNum = parseInt(b.replace(/[^\d]/g, '')) || 0;
      return aLineNum - bLineNum;
    })
    .slice(0, 20) // Limit to 20 groups
    .reduce((acc, [groupName, machines]) => {
      acc[groupName] = machines as Machine[];
      return acc;
    }, {} as Record<string, Machine[]>);

  // Update group order when groups change
  useEffect(() => {
    const currentGroupNames = Object.keys(groupedOnly);
    if (currentGroupNames.length > 0 && groupOrder.length === 0) {
      // Initialize group order if not set
      setGroupOrder(currentGroupNames);
    } else if (currentGroupNames.length > 0) {
      // Update group order to include new groups
      const newGroups = currentGroupNames.filter(name => !groupOrder.includes(name));
      if (newGroups.length > 0) {
        setGroupOrder(prev => [...prev, ...newGroups]);
      }
    }
  }, [groupedOnly, groupOrder]);

  // Auto-select first group on mobile when groups change
  useEffect(() => {
    const groupNames = Object.keys(groupedOnly);
    if (groupNames.length > 0) {
      // Keep an explicit "Ungrouped" selection - it is never in groupNames
      if (mobileSelectedGroup !== '__ungrouped__' && (!mobileSelectedGroup || !groupNames.includes(mobileSelectedGroup))) {
        setMobileSelectedGroup(groupNames[0]);
      }
    } else if ((groupedMachines['Ungrouped Machines'] || []).length > 0 && mobileSelectedGroup !== '__ungrouped__') {
      // No groups at all - fall back to the ungrouped machines
      setMobileSelectedGroup('__ungrouped__');
    }
  }, [groupedOnly, groupedMachines, mobileSelectedGroup]);

  const ungroupedMachines = (groupedMachines['Ungrouped Machines'] || []);

  // MiniSparkline component for efficiency trend - Heartbeat/EKG style
  const MiniSparkline = React.memo(({ data, color }: { data: number[]; color: string }) => {
    const [animatedLength, setAnimatedLength] = useState(0);
    const [pathLength, setPathLength] = useState(200);
    const pathRef = useRef<SVGPathElement>(null);
    const animationIdRef = useRef<number | null>(null);

    useEffect(() => {
      if (!data || data.length === 0) return;

      // Reset animation
      setAnimatedLength(0);

      // Wait for path to render, then get length
      const timer = setTimeout(() => {
        if (pathRef.current) {
          const length = pathRef.current.getTotalLength();
          setPathLength(length);

          // Animate the line drawing (heartbeat effect)
          const duration = 2000; // 2 seconds
          const startTime = Date.now();

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function for smooth heartbeat effect
            const eased = progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;

            setAnimatedLength(length * eased);

            if (progress < 1) {
              animationIdRef.current = requestAnimationFrame(animate);
            }
          };

          animationIdRef.current = requestAnimationFrame(animate);
        }
      }, 50);

      return () => {
        clearTimeout(timer);
        if (animationIdRef.current !== null) {
          cancelAnimationFrame(animationIdRef.current);
        }
      };
    }, [data]);

    if (!data || data.length === 0) return null;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    // Create path string
    const pathData = data.map((val: number, i: number) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 100 - ((val - min) / range) * 100;
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    }).join(' ');

    // Calculate cursor position for moving dot
    const progress = pathLength > 0 ? animatedLength / pathLength : 0;
    const cursorIndex = Math.floor(progress * (data.length - 1));
    const cursorX = cursorIndex >= 0 && cursorIndex < data.length
      ? (cursorIndex / (data.length - 1)) * 100
      : 0;
    const cursorY = cursorIndex >= 0 && cursorIndex < data.length
      ? 100 - ((data[cursorIndex] - min) / range) * 100
      : 50;

    return (
      <div className="relative w-full h-8 overflow-hidden bg-gray-50/30 rounded">
        <svg className="w-full h-8" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Background grid lines for EKG effect */}
          <line x1="0" y1="25" x2="100" y2="25" stroke="currentColor" strokeWidth="0.3" className="text-gray-200" strokeDasharray="1,1" />
          <line x1="0" y1="50" x2="100" y2="50" stroke="currentColor" strokeWidth="0.3" className="text-gray-200" strokeDasharray="1,1" />
          <line x1="0" y1="75" x2="100" y2="75" stroke="currentColor" strokeWidth="0.3" className="text-gray-200" strokeDasharray="1,1" />

          {/* Heartbeat line with drawing animation */}
          <path
            ref={pathRef}
            d={pathData}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={color}
            style={{
              strokeDasharray: pathLength,
              strokeDashoffset: pathLength - animatedLength,
              filter: 'drop-shadow(0 0 1px currentColor)',
            }}
          />

          {/* Moving cursor dot (heartbeat indicator) */}
          {animatedLength > 0 && progress < 1 && (
            <circle
              cx={cursorX}
              cy={cursorY}
              r="3"
              fill="currentColor"
              className={color}
              style={{
                filter: 'drop-shadow(0 0 3px currentColor)',
                animation: 'heartbeat-pulse 0.6s ease-in-out infinite'
              }}
            />
          )}

          {/* Continuous subtle pulse effect */}
          <path
            d={pathData}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={color}
            style={{
              opacity: 0.2,
              animation: 'heartbeat-glow 2.5s ease-in-out infinite',
            }}
          />
        </svg>

        {/* CSS animations */}
        <style>{`
          @keyframes heartbeat-pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.4);
            }
          }
          @keyframes heartbeat-glow {
            0%, 100% {
              opacity: 0.2;
            }
            50% {
              opacity: 0.35;
            }
          }
        `}</style>
      </div>
    );
  });

  const EfficiencyChart = ({ data, color, height = 60 }: { data: number[]; color: string; height?: number }) => {
    // Extract hex color from tailwind text class
    const getColorHex = (colorClass: string) => {
      if (colorClass.includes('emerald') || colorClass.includes('green')) return '#16a34a'; // green-600
      if (colorClass.includes('amber') || colorClass.includes('orange')) return '#ea580c'; // orange-600
      if (colorClass.includes('rose') || colorClass.includes('red')) return '#dc2626'; // red-600
      if (colorClass.includes('slate')) return '#64748b'; // slate-500
      if (colorClass.includes('gray')) return '#6b7280'; // gray-500
      if (colorClass.includes('blue')) return '#0060b9'; // blue-600
      return '#0060b9';
    };

    const hexColor = getColorHex(color);

    const options: ApexOptions = {
      chart: {
        type: 'area',
        sparkline: {
          enabled: true
        },
        animations: {
          enabled: true,
          speed: 800,
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: [hexColor]
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [0, 100],
          colorStops: [
            {
              offset: 0,
              color: hexColor,
              opacity: 0.45
            },
            {
              offset: 100,
              color: hexColor,
              opacity: 0.05
            }
          ]
        }
      },
      tooltip: {
        theme: 'light',
        fixed: {
          enabled: false
        },
        x: {
          show: false
        },
        y: {
          title: {
            formatter: () => 'Eff'
          }
        },
        marker: {
          show: false
        }
      },
      colors: [hexColor],
    };

    const series = [{
      name: 'Efficiency',
      data: data
    }];

    return (
      <div className="w-full relative">
        <div className="filter drop-shadow-sm">
          <Chart options={options} series={series} type="area" height={height} width="100%" />
        </div>
      </div>
    );
  };

  // Determine status category from machine status for theme mapping
  const getStatusCategory = (machineStatus: { text: string; color: string }, isOn: boolean): 'running' | 'low' | 'maintenance' | 'stopped' | 'offline' => {
    if (!isOn && machineStatus.color === config.specialStatuses.offline.color) return 'offline';
    if (!isOn && machineStatus.color === config.specialStatuses.manual.color) return 'maintenance';
    if (!isOn && (machineStatus.color === config.specialStatuses.fault.color || machineStatus.color === config.specialStatuses.stopped.color)) return 'stopped';

    // For efficiency-based statuses, check which range matches
    const sorted = [...config.efficiencyRanges].sort((a, b) => b.min - a.min);
    if (sorted.length > 0 && machineStatus.color === sorted[0].color) return 'running';
    if (sorted.length > 1 && machineStatus.color === sorted[1].color) return 'low';

    // Fallback: if the machine is on, use 'low' for sub-optimal; otherwise 'stopped'
    if (isOn) return 'low';
    return 'stopped';
  };

  // Status config mapping for Dashboard-style cards
  const getStatusConfig = (machineStatus: { text: string; color: string }, isOn: boolean) => {
    const category = getStatusCategory(machineStatus, isOn);

    // Professional color palette - dark borders/badges with light backgrounds
    const palette = {
      running: {
        bg: 'bg-[#F0FDF4]',
        accent: 'border-[#15803D]',
        accentBg: 'bg-[#15803D]',
        text: 'text-slate-900',
        gauge: 'bg-[#15803D]',
        chip: 'bg-[#15803D] text-white'
      },
      low: {
        bg: 'bg-[#FFF7ED]',
        accent: 'border-[#C2410C]',
        accentBg: 'bg-[#C2410C]',
        text: 'text-slate-900',
        gauge: 'bg-[#C2410C]',
        chip: 'bg-[#C2410C] text-white'
      },
      maintenance: {
        bg: 'bg-[#EFF6FF]',
        accent: 'border-[#1E40AF]',
        accentBg: 'bg-[#1E40AF]',
        text: 'text-slate-900',
        gauge: 'bg-[#1E40AF]',
        chip: 'bg-[#1E40AF] text-white'
      },
      stopped: {
        bg: 'bg-[#FEF2F2]',
        accent: 'border-[#B91C1C]',
        accentBg: 'bg-[#B91C1C]',
        text: 'text-slate-900',
        gauge: 'bg-[#B91C1C]',
        chip: 'bg-[#B91C1C] text-white'
      },
      offline: {
        bg: 'bg-[#F9FAFB]',
        accent: 'border-[#4B5563]',
        accentBg: 'bg-[#4B5563]',
        text: 'text-slate-700',
        gauge: 'bg-[#4B5563]',
        chip: 'bg-[#4B5563] text-white'
      },
    };

    // Factory-grade UI palette mapping
    if (category === 'running') {
      return {
        label: machineStatus.text,
        cardBg: palette.running.bg,
        border: 'border-slate-200',
        accentBg: palette.running.accentBg,
        iconBg: 'bg-slate-200',
        badgeBg: palette.running.chip,
        badgeBorder: palette.running.accent,
        text: palette.running.text,
        gaugeColor: palette.running.gauge,
        icon: PlayCircle,
        pulse: true,
        glow: '',
      };
    }
    if (category === 'low') {
      return {
        label: machineStatus.text,
        cardBg: palette.low.bg,
        border: 'border-slate-200',
        accentBg: palette.low.accentBg,
        iconBg: 'bg-slate-200',
        badgeBg: palette.low.chip,
        badgeBorder: palette.low.accent,
        text: palette.low.text,
        gaugeColor: palette.low.gauge,
        icon: TrendingUp,
        pulse: true,
        glow: '',
      };
    }
    if (category === 'maintenance') {
      return {
        label: machineStatus.text,
        cardBg: palette.maintenance.bg,
        border: 'border-slate-200',
        accentBg: palette.maintenance.accentBg,
        iconBg: 'bg-slate-200',
        badgeBg: palette.maintenance.chip,
        badgeBorder: palette.maintenance.accent,
        text: palette.maintenance.text,
        gaugeColor: palette.maintenance.gauge,
        icon: Settings,
        pulse: false,
        glow: '',
      };
    }
    if (category === 'offline') {
      return {
        label: machineStatus.text,
        cardBg: palette.offline.bg,
        border: 'border-slate-200',
        accentBg: palette.offline.accentBg,
        iconBg: 'bg-slate-200',
        badgeBg: palette.offline.chip,
        badgeBorder: palette.offline.accent,
        text: palette.offline.text,
        gaugeColor: palette.offline.gauge,
        icon: Power,
        pulse: false,
        glow: '',
      };
    }
    // Fault / Stopped
    return {
      label: machineStatus.text,
      cardBg: palette.stopped.bg,
      border: 'border-slate-200',
      accentBg: palette.stopped.accentBg,
      iconBg: 'bg-slate-200',
      badgeBg: palette.stopped.chip,
      badgeBorder: palette.stopped.accent,
      text: palette.stopped.text,
      gaugeColor: palette.stopped.gauge,
      icon: AlertTriangle,
      pulse: true,
      glow: '',
    };
  };

  // Machine Card Component
  const MachineCard = ({
    machine,
    bgColor,
    isRowLoading = false,
    isRowApiLoading = false,
    handleMachineSettings,
    handleExportReport,
    handlePickEdit,
    handleEfficiencyEdit,
    setDeletingMachine,
    getMachineWithStatus,
    shouldHighlightMachine,
    getMachineStatus,
  }: {
    machine: Machine;
    bgColor: { text: string; color: string }; // { text: 'Running', color: 'rgb(13, 232, 93)' }
    isRowLoading?: boolean;
    isRowApiLoading?: boolean;
    handleMachineSettings: (machine: Machine) => void;
    handleExportReport: (machine: Machine) => void;
    handlePickEdit: (machine: Machine) => void;
    handleEfficiencyEdit: (machine: Machine) => void;
    setDeletingMachine: (machine: Machine | null) => void;
    getMachineWithStatus: (machine: Machine) => Machine;
    shouldHighlightMachine: (machine: Machine) => boolean;
    getMachineStatus: (machine: Machine) => { text: string; color: string };
  }) => {
    const machineWithStatus = getMachineWithStatus(machine);
    const shouldHighlight = shouldHighlightMachine(machine);
    const machineStatus = getMachineStatus(machine);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const machineDropdownOpen = isDropdownOpen(machine.id);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(machine.id, false);
        }
      };

      if (machineDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [machineDropdownOpen, machine.id, setIsDropdownOpen]);

    const efficiency = Math.max(0, Math.min(100, machine.efficiency || 0));
    const config = getStatusConfig(machineStatus, machineWithStatus.isOn);
    const Icon = config.icon;

    // One status color drives the whole card. Derive a readable pill color and a
    // darker number color from it (the status color may be #hex or rgb()).
    const statusColor = bgColor.color as string;
    const category = getStatusCategory(machineStatus, machineWithStatus.isOn);
    const pillColor = shade(statusColor, 0.82);   // deeper so white text stays legible
    const numberColor = shade(statusColor, 0.68); // dark enough for numbers on white
    const headerTint = withAlpha(statusColor, 0.08);
    const ringTrack = '#e2e8f0';
    const fmt = (n: number) => Math.round(n || 0).toLocaleString();

    // Calculate metrics
    const totalMeters = machine.machineType === 'rapier_jacket'
      ? (() => {
        const rpm = machine.rpm || 0;
        const eff = machine.efficiency || 0;
        return rpm * 60 * 24 * (eff / 100);
      })()
      : (machine.totalMeters || 0);

    // Format online hours
    const formatHours = (hours: number) => {
      if (!hours || hours === 0) return '0h 0m';
      const h = Math.floor(hours);
      const m = Math.floor((hours - h) * 60);
      return `${h}h ${m}m`;
    };

    const resolved = resolveStatus(
      machine.efficiency || 0,
      machineWithStatus.isOn,
      machine.isStale,
      machine.reason || 'ignored',
      machine.efficiencyThreshold || 95
    );
    // Full status words for the pill, shortened "Efficiency" -> "Eff" (LOW EFF / POOR EFF).
    const statusText = (resolved.statusText || machineStatus.text || '').replace(/Efficiency/i, 'Eff');

    const isRunning = machineStatus.text === 'Running';

    // Superadmin "highlight stopped cards" feature: a stopped (manual/fault) card shows a
    // red live "stopped for" banner and blinks after STOP_BLINK_AFTER_MS. stopStateFor
    // supplies the stop-start (server time, or a client fallback until it arrives) so the
    // banner/blink don't wait on the time-series DB lag; offline/stale/running are excluded.
    const { stopped: highlightStopped, effStart: stopStart, blinkCandidate } = stopStateFor(machine, machineWithStatus.isOn);
    const stoppedLongEnough = useStoppedLongEnough(stopStart, blinkCandidate);
    const blinkStopped = blinkCandidate && stoppedLongEnough;

    return (
      <div className={'col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-3 relative'} data-machine-id={machine.id}>
        <div
          className={`group relative flex flex-col overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md${blinkStopped ? ' machine-card-stopped' : ''}`}
          style={{
            background: '#ffffff',
            border: '1px solid #dde3ea',
            borderTop: `5px solid ${statusColor}`,
            borderRadius: '12px',
          }}
        >
          {(isRowLoading || isRowApiLoading) && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-20 pointer-events-none rounded-xl">
              <div className="flex items-center gap-2.5 text-sm font-medium text-slate-600">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-200 border-t-slate-500"></div>
                <span>Loading...</span>
              </div>
            </div>
          )}

            {/* Header — status-tinted */}
            <div
              className="flex items-center justify-between gap-2"
              style={{ padding: '9px 14px', background: headerTint, borderBottom: '1px solid #e2e8f0' }}
            >
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[19px] font-bold text-slate-900 leading-none">#{machine.number}</span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">
                    {machine.machineType === 'rapier_jacket' ? 'Rapier' : 'Water Jet'}
                  </span>
                </div>
                {showCompanyColumn && (
                  <span className="block text-[10px] font-semibold text-blue-600 mt-0.5 truncate">
                    <CompanyCell tenantId={machine.tenantId} />
                  </span>
                )}
              </div>
              <span
                className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white flex-shrink-0"
                style={{ padding: '4px 10px', background: pillColor }}
              >
                {isRunning && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                  </span>
                )}
                <Icon className="w-3 h-3" />
                {statusText}
              </span>
            </div>

            {/* Stopped highlight banner — live "stopped for" timer + today's stop count */}
            {highlightStopped && (
              <StoppedBanner startedAt={stopStart} stopCount={machine.stopCount} />
            )}

            {/* Donut + shift rows */}
            <div className="flex items-center gap-3.5" style={{ padding: '12px 14px' }}>
              {/* Efficiency donut (click to adjust threshold) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEfficiencyEdit(machine);
                }}
                title="Click to adjust efficiency threshold"
                aria-label={`Efficiency ${Math.round(efficiency)} percent`}
                className="flex-shrink-0 transition-transform hover:scale-105"
                style={{
                  width: '78px',
                  height: '78px',
                  borderRadius: '50%',
                  background: `conic-gradient(${statusColor} ${efficiency}%, ${ringTrack} 0)`,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <span
                  className="flex flex-col items-center justify-center"
                  style={{ width: '62px', height: '62px', borderRadius: '50%', background: '#ffffff', gap: '2px', lineHeight: 1 }}
                >
                  <span className="font-mono font-bold" style={{ fontSize: '22px', color: numberColor }}>{Math.round(efficiency)}</span>
                  <span className="font-semibold tracking-widest" style={{ fontSize: '9px', color: '#64748b' }}>% EFF</span>
                </span>
              </button>

              {/* Shift rows (fault/stopped shows a reason banner instead of the day row) */}
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                {category === 'stopped' && machine.reason && machine.reason !== 'ignored' ? (
                  <div className="flex items-center gap-2 rounded-lg" style={{ padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#b91c1c' }} />
                    <span className="text-[12px] font-bold truncate" style={{ color: '#991b1b' }}>Stopped · {machine.reason}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-lg" style={{ padding: '6px 10px', background: '#fffbeb' }}>
                    <Sun className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#b45309' }} />
                    <span className="text-[11px] font-bold tracking-wider flex-shrink-0" style={{ width: '38px', color: '#92400e' }}>DAY</span>
                    <span className="flex-1 font-mono text-[13px] font-semibold text-slate-800 truncate">{fmt(machine.meters12HourDay || 0)} m</span>
                    <span className="font-mono text-[13px] font-bold flex-shrink-0" style={{ color: (machine.meters12HourDay || 0) > 0 ? numberColor : '#475569' }}>{Math.round(machine.dayShiftEfficiency || 0)}%</span>
                  </div>
                )}
                <div className="flex items-center gap-2 rounded-lg" style={{ padding: '6px 10px', background: '#eff6ff' }}>
                  <Moon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#1d4ed8' }} />
                  <span className="text-[11px] font-bold tracking-wider flex-shrink-0" style={{ width: '38px', color: '#1e40af' }}>NIGHT</span>
                  <span className="flex-1 font-mono text-[13px] font-semibold text-slate-800 truncate">{fmt(machine.meters12HourNight || 0)} m</span>
                  <span className="font-mono text-[13px] font-bold flex-shrink-0" style={{ color: (machine.meters12HourNight || 0) > 0 ? numberColor : '#475569' }}>{Math.round(machine.nightShiftEfficiency || 0)}%</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePickEdit(machine);
                  }}
                  title="Click to edit pick value"
                  className="flex items-center justify-between rounded-lg hover:bg-slate-100 transition-colors w-full"
                  style={{ padding: '4px 10px', background: '#f8fafc' }}
                >
                  <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Pick</span>
                  <span className="font-mono text-[13px] font-bold text-slate-700">{machine.pickValue || 1}</span>
                </button>
              </div>
            </div>

            {/* Metrics — row 1: RPM / meters / stops; row 2: run vs offline hours */}
            <div className="grid grid-cols-3" style={{ borderTop: '1px solid #e2e8f0' }}>
              <div className="text-center" style={{ padding: '8px 4px', borderRight: '1px solid #e2e8f0' }}>
                <div className="font-mono text-[15px] font-bold text-slate-900 leading-tight">{machine.rpm ?? 0}</div>
                <div className="text-[10px] font-semibold tracking-wider text-slate-500">RPM</div>
              </div>
              <div className="text-center" style={{ padding: '8px 4px', borderRight: '1px solid #e2e8f0' }}>
                <div className="font-mono text-[15px] font-bold text-slate-900 leading-tight">{fmt(totalMeters)}</div>
                <div className="text-[10px] font-semibold tracking-wider text-slate-500">24H m</div>
              </div>
              {/* Stops — clickable. Opening the list also stops this card's blink for this user. */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  acknowledgeStop(machine);
                  setSelectedMachineForStopEvents(machine);
                  setShowStopEventsModal(true);
                  handleLoadStopEvents(machine.id);
                }}
                className="text-center transition-colors hover:brightness-95"
                style={{ padding: '8px 4px', background: (machine.stopCount || 0) > 0 ? '#fef2f2' : 'transparent' }}
              >
                <div className="font-mono text-[15px] font-bold leading-tight" style={{ color: (machine.stopCount || 0) > 0 ? '#b91c1c' : '#0f172a' }}>
                  {loadingStopEventsMachineId === machine.id ? (
                    <span className="inline-flex items-center justify-center">
                      <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-red-200 border-t-red-500"></span>
                    </span>
                  ) : (
                    machine.stopCount || 0
                  )}
                </div>
                <div className="text-[10px] font-semibold tracking-wider" style={{ color: (machine.stopCount || 0) > 0 ? '#b91c1c' : '#64748b' }}>STOPS</div>
              </button>
            </div>
            <div className="grid grid-cols-2" style={{ borderTop: '1px solid #e2e8f0' }}>
              <div className="text-center" style={{ padding: '8px 4px', borderRight: '1px solid #e2e8f0' }}>
                <div className="font-mono text-[15px] font-bold text-slate-900 leading-tight whitespace-nowrap">{machine.onlineHours || '0h 0m'}</div>
                <div className="text-[10px] font-semibold tracking-wider text-slate-500">RUN HRS</div>
              </div>
              <div className="text-center" style={{ padding: '8px 4px' }}>
                <div className="font-mono text-[15px] font-bold text-slate-500 leading-tight whitespace-nowrap">{machine.offlineHours || '0h 0m'}</div>
                <div className="text-[10px] font-semibold tracking-wider text-slate-500">OFFLINE HRS</div>
              </div>
            </div>

            {/* Beam Section */}
            {hasModulePermission('beams') && (
            <div style={{ padding: '12px 14px' }}>
            {(() => {
              if (machine.beamNumber) {
              const expBeamRemaining = Math.max(0, (machine.beamEffectiveMeters || 0) - (machine.beamMetersUsed || 0));
              const expBeamEmpty = expBeamRemaining <= 0;
              const expBeamPercent = Math.min(100, ((machine.beamMetersUsed || 0) / (machine.beamEffectiveMeters || 1)) * 100);
              const expBeamDailyAvg = machine.totalMeters && machine.totalMeters > 0 ? machine.totalMeters : 0;
              const expBeamDaysLeft = expBeamDailyAvg > 0 ? expBeamRemaining / expBeamDailyAvg : null;
              const expWarningLevel = expBeamEmpty ? 'empty' : expBeamDaysLeft !== null
                ? expBeamDaysLeft <= 1 ? 'critical' : expBeamDaysLeft <= 4 ? 'warning' : expBeamDaysLeft <= 7 ? 'info' : null : null;
              return (
              <div className={`rounded-xl overflow-hidden border ${
                expBeamEmpty ? 'border-red-300 ring-2 ring-red-400/40 shadow-lg shadow-red-100' :
                expWarningLevel === 'critical' ? 'bg-red-50/70 border-red-200' :
                expWarningLevel === 'warning' ? 'bg-amber-50/70 border-amber-200' :
                'bg-blue-50/60 border-blue-100/80'
              }`}>
                {/* Beam empty big alert header */}
                {expBeamEmpty ? (
                  <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 px-3 py-3 flex items-center gap-3">
                    <Cable className="w-8 h-8 text-white flex-shrink-0"  />
                    <div className="flex-1">
                      <div className="text-[12px] font-extrabold text-white uppercase tracking-wider">Beam Empty!</div>
                      <div className="text-[9px] font-bold text-red-200">Beam {machine.beamNumber} is fully used. Please detach and attach a new beam.</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetachBeam(machine);
                      }}
                      className="text-[10px] font-extrabold text-red-600 bg-white px-3 py-1.5 rounded-lg uppercase tracking-wider hover:bg-red-50 transition-colors shadow-sm flex-shrink-0"
                    >
                      Detach
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 pb-2">
                    <div className="flex items-center gap-1.5">
                      {expWarningLevel === 'critical' ? (
                        <Cable className="w-4 h-4 text-red-500" />
                      ) : expWarningLevel === 'warning' ? (
                        <Cable className="w-4 h-4 text-amber-500" />
                      ) : (
                        <Cable className="w-4 h-4 text-blue-500" />
                      )}
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        expWarningLevel === 'critical' ? 'text-red-600' :
                        expWarningLevel === 'warning' ? 'text-amber-600' :
                        'text-blue-600'
                      }`}>
                        Beam {machine.beamNumber}
                      </span>
                      {machine.beamName && <span className="text-[10px] text-blue-400">({machine.beamName})</span>}
                      {expWarningLevel && (
                        <span className={`text-[8px] font-extrabold px-1.5 py-0.5 rounded ${
                          expWarningLevel === 'critical' ? 'bg-red-100 text-red-700 animate-pulse' :
                          expWarningLevel === 'warning' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {expBeamDaysLeft !== null ? `~${expBeamDaysLeft.toFixed(0)}d left` : ''}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetachBeam(machine);
                      }}
                      className="text-[9px] font-bold text-red-400 hover:text-red-600 uppercase tracking-wider"
                    >
                      Detach
                    </button>
                  </div>
                )}
                <div className={`${expBeamEmpty ? 'px-3 py-2 bg-red-50' : 'px-3 pb-3'}`}>
                  <div className="flex items-baseline justify-between">
                    <span className={`text-sm font-black font-mono ${
                      expBeamEmpty ? 'text-red-700' : 'text-slate-800'
                    }`}>
                      {expBeamRemaining.toFixed(0)}m left
                    </span>
                    <span className="text-[10px] font-mono text-slate-500">
                      {(machine.beamMetersUsed || 0).toFixed(0)} / {(machine.beamEffectiveMeters || 0).toFixed(0)}m
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className={`mt-1.5 h-1.5 w-full rounded-full overflow-hidden ${
                    expBeamEmpty ? 'bg-red-200' :
                    expWarningLevel === 'critical' ? 'bg-red-100' :
                    expWarningLevel === 'warning' ? 'bg-amber-100' :
                    'bg-blue-100'
                  }`}>
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        expBeamEmpty ? 'bg-red-500' :
                        expWarningLevel === 'critical' ? 'bg-red-500' :
                        expWarningLevel === 'warning' ? 'bg-amber-500' :
                        'bg-blue-500'
                      }`}
                      style={{ width: `${expBeamPercent}%` }}
                    />
                  </div>
                </div>
              </div>
              );
            }
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMachineForBeam(machine);
                  setBeamForm({ beamNumber: '', beamName: '', beamTotalMeters: '', shortagePercentage: '' });
                  setShowBeamModal(true);
                }}
                className="w-full rounded-xl border border-red-600 bg-red-500 p-3 text-center hover:bg-red-600 hover:shadow-md transition-all group/beam"
              >
                <div className="flex items-center justify-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-white/20 group-hover/beam:bg-white/30 flex items-center justify-center transition-colors">
                    <Cable className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <span className="text-[11px] font-bold text-white block">Attach Beam</span>
                    <span className="text-[9px] text-red-100">Tap to add beam data</span>
                  </div>
                </div>
              </button>
            );
            })()}
            </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between" style={{ padding: '7px 14px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleExportReport(machine);
                }}
                className="inline-flex items-center gap-1.5 rounded-md transition-colors hover:bg-slate-50"
                style={{ padding: '5px 10px', border: '1px solid #cbd5e1', background: '#ffffff' }}
              >
                <Download className="w-3 h-3 text-slate-600" />
                <span className="text-[12px] font-semibold text-slate-700">Export</span>
              </button>
              <span className="text-[11px] text-slate-500">
                Updated <span className="font-mono font-semibold text-slate-700">{machine.lastUpdate || 'N/A'}</span>
              </span>
            </div>
          </div>
        </div>
    );
  };

  // Handle loading stop events
  const handleLoadStopEvents = async (machineId: number) => {
    setLoadingStopEventsMachineId(machineId);
    try {
      const path =
        statusRange === 'lastDay'
          ? `/machine-logs/${machineId}/stop-events/last-day`
          : `/machine-logs/${machineId}/stop-events`;
      const response = await api.get(path);
      if (response.data.success) {
        setStopEvents(response.data.data.stopEvents || []);
        setStopEventsData({
          from: response.data.data.from,
          to: response.data.data.to,
          stopCount: response.data.data.stopCount,
          totalStopTimeFormatted: response.data.data.totalStopTimeFormatted
        });
      }
    } catch (error) {
      console.error('Error loading stop events:', error);
      toast.error('Failed to load stop events');
      setStopEvents([]);
      setStopEventsData(null);
    } finally {
      setLoadingStopEventsMachineId(null);
    }
  };

  const handleSaveStopNote = async (machineId: number, stoppedAt: string, reason: string, note: string) => {
    setSavingNote(true);
    try {
      const response = await api.post(`/machines/${machineId}/stop-notes`, {
        stoppedAt,
        reason,
        note: note || null
      });
      if (response.data.success) {
        // Update the stop event in local state
        setStopEvents(prev => prev.map(event => {
          if (event.stoppedAt === stoppedAt) {
            return { ...event, stopNote: { reason, note, noteId: response.data.data.id } };
          }
          return event;
        }));
        toast.success('Stop note saved');
        setEditingNoteIdx(null);
        setNoteForm({ reason: '', note: '' });
      }
    } catch (error) {
      console.error('Error saving stop note:', error);
      toast.error('Failed to save stop note');
    } finally {
      setSavingNote(false);
    }
  };

  // Compact machine tile — the dense status card shown in both grouped and ungrouped
  // grids. Clicking it opens the full detail card (MachineCard) in a modal.
  const MachineTile = ({ machine }: { machine: Machine }) => {
                  const efficiency = Math.round(machine.efficiency || 0);
                  const bgColor = getBgColor(machine);
                  const statusColor = bgColor.color;
                  const statusLabel = bgColor.text;
                  const isRunning = statusLabel === 'RUN';
                  // Same stopped state/blink as MachineCard (see stopStateFor). effStart is
                  // the server stop-start, or a client fallback until it arrives, so the live
                  // timer + blink show the moment the tile goes stopped (manual / fault) rather
                  // than waiting on the time-series DB. Offline/stale/running are excluded.
                  const { stopped, effStart, blinkCandidate } = stopStateFor(machine, getMachineWithStatus(machine).isOn);
                  const stoppedLongEnough = useStoppedLongEnough(effStart, blinkCandidate);
                  const blinkStopped = blinkCandidate && stoppedLongEnough;
                  const showStopTimer = stopped && effStart != null;
                  const now = new Date();
                  const istHour = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).getHours();
                  const isDayShift = istHour >= 8 && istHour < 19;

                  const totalMetersFormatted = Number(machine.totalMeters || 0).toLocaleString('en-IN', {
                    maximumFractionDigits: 0
                  });

                  // Beam warning calculation
                  const beamRemaining = machine.beamNumber && machine.beamEffectiveMeters
                    ? Math.max(0, (machine.beamEffectiveMeters || 0) - (machine.beamMetersUsed || 0))
                    : null;
                  const beamDailyAvg = machine.totalMeters && machine.totalMeters > 0 ? machine.totalMeters : 0;
                  const beamDaysLeft = beamRemaining !== null && beamDailyAvg > 0 ? beamRemaining / beamDailyAvg : null;
                  const beamEmpty = beamRemaining !== null && beamRemaining <= 0;
                  const beamWarningColor = beamEmpty ? '#EF4444'
                    : beamDaysLeft !== null
                      ? beamDaysLeft <= 1 ? '#EF4444' : beamDaysLeft <= 4 ? '#F59E0B' : beamDaysLeft <= 7 ? '#3B82F6' : null
                      : null;
                  const beamWarningLevel: 'empty' | 'critical' | 'warning' | 'info' | null = beamEmpty ? 'empty'
                    : beamDaysLeft !== null
                      ? beamDaysLeft <= 1 ? 'critical' : beamDaysLeft <= 4 ? 'warning' : beamDaysLeft <= 7 ? 'info' : null
                      : null;

                  return (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMachineForCardModal(machine);
                        setShowMachineCardModal(true);
                      }}
                      className={`cursor-pointer group relative rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-2xl touch-manipulation flex flex-col h-full ${
                        beamEmpty ? 'border-3 border-red-500 ring-4 ring-red-400/50 shadow-xl shadow-red-200 bg-red-50 animate-pulse' :
                        beamWarningLevel === 'critical' ? 'border-2 border-red-400 ring-2 ring-red-300/30 shadow-lg shadow-red-100 bg-white' :
                        'border border-gray-200 bg-white'
                      }${blinkStopped ? ' machine-card-stopped' : ''}`}
                    >
                      {/* Beam Empty Full-Width Banner */}
                      {hasModulePermission('beams') && beamEmpty && (
                        <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 px-3 py-2.5 flex items-center justify-center gap-3">
                          <Cable className="w-6 h-6 text-white"  />
                          <div className="flex flex-col items-center">
                            <span className="text-[11px] font-extrabold text-white uppercase tracking-widest">Beam Empty!</span>
                            <span className="text-[8px] font-bold text-red-200 uppercase tracking-wider">Detach & Replace</span>
                          </div>
                          <Cable className="w-6 h-6 text-white"  />
                        </div>
                      )}

                      {/* Header: Machine # + Status - colored background */}
                      <div
                        className="relative px-3 py-2.5 flex justify-between items-center"
                        style={{ backgroundColor: statusColor }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/15 to-black/5 pointer-events-none" />
                        <div className="relative z-10 flex items-center gap-1.5">
                          <div className="flex flex-col">
                            <span className="text-lg font-black font-mono text-white drop-shadow-md leading-none">#{machine.number}</span>
                            {showCompanyColumn && (
                              <span className="text-[8px] font-semibold text-white/80 drop-shadow-md leading-none mt-0.5">
                                <CompanyCell tenantId={machine.tenantId} />
                              </span>
                            )}
                          </div>
                          {hasModulePermission('beams') && beamWarningLevel && (
                            <span
                              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[8px] font-extrabold uppercase tracking-wider ${
                                beamWarningLevel === 'empty' ? 'animate-pulse bg-red-500 text-white' :
                                beamWarningLevel === 'critical' ? 'animate-pulse bg-red-500/90 text-white' :
                                beamWarningLevel === 'warning' ? 'bg-amber-400 text-amber-900' :
                                'bg-blue-400/80 text-white'
                              }`}
                              title={beamEmpty ? `Beam EMPTY! (0m remaining)` : `Beam: ~${beamDaysLeft?.toFixed(1)} days left (${beamRemaining?.toFixed(0)}m remaining)`}
                            >
                              {beamWarningLevel === 'empty' ? (
                                <><Cable className="w-3.5 h-3.5"  /> EMPTY</>
                              ) : beamWarningLevel === 'critical' ? (
                                <><Cable className="w-3 h-3" /> {beamDaysLeft?.toFixed(0)}d</>
                              ) : (
                                <><Cable className="w-2.5 h-2.5" /> {beamDaysLeft?.toFixed(0)}d</>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="relative z-10 flex items-center gap-1.5">
                          {isRunning && (
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                            </span>
                          )}
                          <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-md tracking-wider bg-black/20 text-white border border-white/20 backdrop-blur-sm">
                            {statusLabel}
                          </span>
                        </div>
                      </div>

                      {/* Stopped highlight banner — live "stopped for" timer + today's stop count */}
                      {showStopTimer && (
                        <StoppedBanner startedAt={effStart} stopCount={machine.stopCount} />
                      )}

                      {/* Body - white/neutral background */}
                      <div className="p-3 flex-grow space-y-2.5">
                        {/* Production + Efficiency */}
                        <div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-2xl font-black font-mono text-slate-800 leading-none">
                              {totalMetersFormatted}
                            </span>
                            <span className="text-sm font-black font-mono" style={{ color: statusColor }}>{efficiency}%</span>
                          </div>
                          {/* Efficiency bar */}
                          <div className="mt-1.5 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(efficiency, 100)}%`, backgroundColor: statusColor }}
                            />
                          </div>
                        </div>

                        {/* Shift Box */}
                        <div className="rounded-xl p-2 bg-slate-50 border border-slate-100">
                          <div className="flex items-center gap-1 mb-1">
                            {isDayShift ? <Sun className="h-3 w-3 text-amber-500" /> : <Moon className="h-3 w-3 text-primary-400" />}
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                              {isDayShift ? 'Day' : 'Night'}
                            </span>
                          </div>
                          <div className="flex justify-between items-baseline gap-1">
                            <span className="text-sm font-black font-mono text-slate-800 truncate">
                              {isDayShift ? (machine.meters12HourDay || 0).toFixed(0) : (machine.meters12HourNight || 0).toFixed(0)}m
                            </span>
                            <span className="text-[11px] font-bold font-mono text-slate-500 flex-shrink-0">
                              {isDayShift ? (machine.dayShiftEfficiency || 0).toFixed(0) : (machine.nightShiftEfficiency || 0).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Beam Info */}
                      {hasModulePermission('beams') && (machine.beamNumber ? (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDetachBeam(machine);
                          }}
                          className={`cursor-pointer px-3 transition-colors ${
                            beamEmpty ? 'bg-red-500 py-2.5 hover:bg-red-600' :
                            beamWarningLevel === 'critical' ? 'bg-red-50/60 border-t border-red-100 py-1.5 hover:bg-red-100/60' :
                            beamWarningLevel === 'warning' ? 'bg-amber-50/60 border-t border-amber-100 py-1.5 hover:bg-amber-100/60' :
                            'border-t border-slate-100 py-1.5 hover:bg-blue-50'
                          }`}
                          title={beamEmpty ? `Beam ${machine.beamNumber} is EMPTY - Click to detach` : `Beam ${machine.beamNumber} - Click to detach`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              {beamEmpty ? (
                                <Cable className="w-5 h-5 text-white"  />
                              ) : beamWarningLevel === 'critical' ? (
                                <Cable className="w-3.5 h-3.5 text-red-500" />
                              ) : beamWarningLevel === 'warning' ? (
                                <Cable className="w-3.5 h-3.5 text-amber-500" />
                              ) : (
                                <Cable className="w-3.5 h-3.5 text-blue-500" />
                              )}
                              <span className={`font-bold uppercase ${
                                beamEmpty ? 'text-[10px] text-white' : 'text-[9px] text-slate-400'
                              }`}>{beamEmpty ? `Beam ${machine.beamNumber} — Empty!` : 'Beam'}</span>
                              {!beamEmpty && <span className="text-[10px] font-bold font-mono text-slate-700">{machine.beamNumber}</span>}
                            </div>
                            {beamEmpty ? (
                              <span className="text-[9px] font-extrabold text-red-600 bg-white px-2.5 py-1 rounded-md uppercase shadow-sm">Detach</span>
                            ) : (
                              <span className={`text-[10px] font-bold font-mono ${
                                beamWarningLevel === 'critical' ? 'text-red-600' :
                                beamWarningLevel === 'warning' ? 'text-amber-600' :
                                'text-blue-600'
                              }`}>
                                {beamRemaining !== null ? `${beamRemaining.toFixed(0)}m` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMachineForBeam(machine);
                            setBeamForm({ beamNumber: '', beamName: '', beamTotalMeters: '', shortagePercentage: '' });
                            setShowBeamModal(true);
                          }}
                          className="cursor-pointer px-3 py-2 bg-red-500 hover:bg-red-600 transition-all"
                          title="Attach beam to machine"
                        >
                          <div className="flex items-center justify-center gap-1.5">
                            <Cable className="w-4 h-4 text-white" />
                            <span className="text-[9px] font-bold text-white uppercase tracking-wide">+ Attach Beam</span>
                          </div>
                        </div>
                      ))}

                      {/* Pick & Threshold — tap to edit */}
                      <div className="px-3 py-1 flex justify-between items-center border-t border-slate-100">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePickEdit(machine);
                          }}
                          className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-blue-50 active:bg-blue-100 transition-colors touch-manipulation"
                          title="Edit Pick Value"
                        >
                          <span className="text-[9px] font-bold text-slate-400 uppercase">Pick</span>
                          <span className="text-xs font-black font-mono text-slate-700">{machine.pickValue || 0}</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEfficiencyEdit(machine);
                          }}
                          className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-blue-50 active:bg-blue-100 transition-colors touch-manipulation"
                          title="Edit Efficiency Threshold"
                        >
                          <span className="text-[9px] font-bold text-slate-400 uppercase">Thr</span>
                          <span className="text-xs font-black font-mono text-slate-700">{machine.efficiencyThreshold || 95}%</span>
                        </button>
                      </div>

                      {/* Footer — tap to view stop events */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          acknowledgeStop(machine); // seen: stop this tile's blink for this user
                          setSelectedMachineForStopEvents(machine);
                          setShowStopEventsModal(true);
                          handleLoadStopEvents(machine.id);
                        }}
                        className="w-full px-3 py-2 bg-slate-50 flex justify-between items-center border-t border-slate-100 mt-auto hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation"
                      >
                        <div className="flex items-center gap-1">
                          <PauseCircle className="w-3 h-3 text-red-400" />
                          <span className="text-[10px] font-bold text-slate-400">STOPS</span>
                          <span className="text-sm font-black font-mono text-red-600">{machine.stopCount || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Activity className="w-3 h-3 text-slate-400" />
                          <span className="text-sm font-bold font-mono text-slate-700">
                            {machine.rpm || 0}
                          </span>
                        </div>
                      </button>
                    </div>
                  );
  };

  // Sortable Group Component
  const SortableGroup = ({ groupName, groupMachines }: { groupName: string; groupMachines: Machine[] }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: groupName });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 1000 : 'auto',
      boxShadow: isDragging ? '0 10px 25px rgba(0, 0, 0, 0.15)' : 'none',
    };

    const groupEfficiency = calculateGroupEfficiency(groupMachines);
    const machineRange = getMachineRange(groupMachines);
    const problematicMachines = getProblematicMachines(groupMachines);
    const hasIssues = problematicMachines.length > 0;
    const isExpanded = expandedGroups.has(groupName);

    // Determine if this group should show loading based on cascading batch loading
    // Find the first batch that is currently loading
    const firstLoadingBatchIndex = batchQueries.findIndex(q => q.isFetching);

    // Check if any machine in this group belongs to a batch that's loading or subsequent batches
    const groupMachineIds = new Set(groupMachines.map((m: Machine) => m.id));
    let shouldShowLoading = false;

    if (firstLoadingBatchIndex >= 0) {
      // Check all batches from firstLoadingBatchIndex onwards (cascading effect)
      for (let batchIdx = firstLoadingBatchIndex; batchIdx < machineBatches.length; batchIdx++) {
        const batch = machineBatches[batchIdx] || [];
        const batchMachineIds = new Set(batch.map((m: Machine) => m.id));

        // If this group has any machines from this batch or subsequent batches, mark it as loading
        const hasOverlap = Array.from(groupMachineIds).some(id => batchMachineIds.has(id));
        if (hasOverlap) {
          shouldShowLoading = true;
          break;
        }
      }
    }

    // Also check if any individual machine in this group is loading
    const hasIndividualLoading = groupMachines.some((m: Machine) => fetchingMachineIds.has(m.id));

    const isGroupLoading = isLoading || shouldShowLoading || hasIndividualLoading;

    return (
      <div ref={setNodeRef} style={style} className="relative">

        <div className={`bg-white rounded-2xl border border-gray-200/80 ${isGroupLoading ? 'opacity-90' : ''} relative overflow-hidden transition-all duration-300`} style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 6px 16px -4px rgba(0,0,0,0.06)' }}>
          {isGroupLoading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-20 rounded-2xl">
              <div className="flex items-center gap-2.5 text-sm font-medium text-slate-600">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-slate-500"></div>
                <span>Loading...</span>
              </div>
            </div>
          )}
          <div className={'px-4 sm:px-6 py-3'}>
            <div className={'flex items-center justify-between gap-4'}>

              {/* Left Section: Toggle + Line Title + Stats */}
              <div className={'flex items-center gap-3 min-w-0'}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleGroupExpansion(groupName);
                  }}
                  className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-all text-gray-400 hover:text-gray-600"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                </button>

                <div className="text-lg font-black text-slate-900 tracking-tight truncate">
                  {groupName}
                </div>

                {/* Issue Badge */}
                {hasIssues && (
                  <div className="relative group flex-shrink-0">
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-[11px] font-extrabold text-red-600">{problematicMachines.length}</span>
                    </div>

                    {/* Tooltip for problematic machines */}
                    <div className="absolute left-0 top-8 z-50 hidden md:group-hover:block bg-slate-900 text-white text-xs px-3 py-2 rounded-xl shadow-2xl whitespace-nowrap border border-white/10">
                      <p className="font-bold mb-1 text-red-400 uppercase tracking-wider text-[9px]">Attention Required</p>
                      {problematicMachines
                        .filter(m => getMachineWithStatus(m).isOn && m.efficiency < (m.efficiencyThreshold || 95))
                        .slice(0, 5)
                        .map(m => `#${m.number} (${m.efficiency.toFixed(1)}%)`)
                        .join(', ')}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Section: Running/Stopped counts + Efficiency */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Running count */}
                <div className="hidden sm:flex items-center gap-1.5 text-xs">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="font-bold text-emerald-600">
                    {groupMachines.filter(m => getMachineWithStatus(m).isOn).length}
                  </span>
                  <span className="text-slate-400 font-medium">run</span>
                </div>

                {/* Stopped count */}
                {groupMachines.filter(m => !getMachineWithStatus(m).isOn).length > 0 && (
                  <div className="hidden sm:flex items-center gap-1.5 text-xs">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="font-bold text-red-500">
                      {groupMachines.filter(m => !getMachineWithStatus(m).isOn).length}
                    </span>
                    <span className="text-slate-400 font-medium">stop</span>
                  </div>
                )}

                {/* Divider */}
                <div className="hidden sm:block w-px h-6 bg-gray-200" />

                {/* Line Efficiency Badge */}
                {(() => {
                  const effColor = groupEfficiency >= 95 ? '#16a34a' : groupEfficiency >= 90 ? '#a855f7' : groupEfficiency >= 60 ? '#eab308' : '#f97316';
                  return (
                    <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl border transition-all duration-500"
                      style={{
                        backgroundColor: `${effColor}0a`,
                        borderColor: `${effColor}30`,
                      }}
                    >
                      <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Eff</span>
                      <span className="font-mono text-xl font-black" style={{ color: effColor }}>
                        {groupEfficiency.toFixed(1)}%
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Machine Grid - Optimized breakpoints */}
            <div className={isExpanded ? 'hidden' : ''}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-8 2xl:grid-cols-10 gap-3 sm:gap-4 mt-4">
                {groupMachines.map(machine => (
                  <MachineTile key={machine.id} machine={machine} />
                ))}
              </div>
            </div>

            {/* Expanded: bigger MachineCards */}
            {isExpanded && (
              <div className="p-5">
                <div className="grid grid-cols-12 gap-2 mt-3">
                  {groupMachines.length > 0 ? (
                    groupMachines.map((machine: Machine) => (
                      <MachineCard
                        key={machine.id}
                        machine={machine}
                        bgColor={getBgColor(machine)}
                        isRowLoading={fetchingMachineIds.has(machine.id)}
                        isRowApiLoading={loadingStopEventsMachineId === machine.id}
                        handleMachineSettings={handleMachineSettings}
                        handleExportReport={handleExportReport}
                        handlePickEdit={handlePickEdit}
                        handleEfficiencyEdit={handleEfficiencyEdit}
                        setDeletingMachine={setDeletingMachine}
                        getMachineWithStatus={getMachineWithStatus}
                        shouldHighlightMachine={shouldHighlightMachine}
                        getMachineStatus={getMachineStatus}
                      />
                    ))
                  ) : !isGroupLoading ? (
                    <div className="col-span-12 px-6 py-12 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                        <Package className="h-8 w-8 text-gray-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Machines</h3>
                      <p className="text-gray-500">This group has no machines assigned.</p>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Group machines by status bucket (the chips' categories). Single source of
  // truth for both the counts shown on the chips and the machine lists shown
  // when a chip is clicked.
  const statusGroups = useMemo(() => {
    const groups: Record<StatusCategoryKey, Machine[]> = {
      running: [], lowEfficiency: [], poorEfficiency: [], problemEfficiency: [],
      fault: [], offline: [], manual: [],
    };
    if (!machinesWithData || machinesWithData.length === 0) return groups;

    // Build lookup: map statusText to its bucket
    const sortedRanges = [...config.efficiencyRanges].sort((a, b) => b.min - a.min);
    const runningText = sortedRanges[0]?.statusText;
    const lowText = sortedRanges[1]?.statusText;
    const poorText = sortedRanges[2]?.statusText;
    const problemText = sortedRanges[3]?.statusText;

    machinesWithData.forEach((machine: Machine) => {
      const status = getMachineStatus(machine);
      if (status.text === runningText) groups.running.push(machine);
      else if (status.text === lowText) groups.lowEfficiency.push(machine);
      else if (status.text === poorText) groups.poorEfficiency.push(machine);
      else if (status.text === problemText) groups.problemEfficiency.push(machine);
      else if (status.text === config.specialStatuses.fault.statusText) groups.fault.push(machine);
      else if (status.text === config.specialStatuses.offline.statusText) groups.offline.push(machine);
      else if (status.text === config.specialStatuses.manual.statusText) groups.manual.push(machine);
    });
    return groups;
  }, [machinesWithData, getMachineStatus, config]);

  // Calculate status counts and average efficiency - MUST be before early returns
  const statusCounts = useMemo(() => {
    let totalEfficiency = 0;
    let efficiencyCount = 0;
    let totalMeters = 0;

    (machinesWithData || []).forEach((machine: Machine) => {
      // Calculate average efficiency
      if (machine.efficiency !== undefined && machine.efficiency !== null) {
        totalEfficiency += machine.efficiency;
        efficiencyCount++;
      }
      // Sum total meters (from API or calculated for rapier_jacket)
      const meters = machine.totalMeters ?? 0;
      totalMeters += typeof meters === 'number' ? meters : parseFloat(String(meters)) || 0;
    });

    return {
      running: statusGroups.running.length,
      lowEfficiency: statusGroups.lowEfficiency.length,
      poorEfficiency: statusGroups.poorEfficiency.length,
      problemEfficiency: statusGroups.problemEfficiency.length,
      fault: statusGroups.fault.length,
      offline: statusGroups.offline.length,
      manual: statusGroups.manual.length,
      avgEfficiency: efficiencyCount > 0 ? totalEfficiency / efficiencyCount : 0,
      totalMeters
    };
  }, [machinesWithData, statusGroups]);

  // TV-mode overlay — portal-rendered at the document body level so it sits above sidebar/header
  const tvOverlay = tvMode ? createPortal(
    (() => {
      // Dynamic color config from DB — same source the machines page uses.
      const sortedRanges = [...config.efficiencyRanges].sort((a, b) => b.min - a.min);
      const runningColor = sortedRanges[0]?.color || '#059669';
      const toRgba = (c: string, a: number): string => {
        if (!c) return `rgba(100,116,139,${a})`;
        const s = c.trim();
        if (s.startsWith('rgba(')) return s;
        if (s.startsWith('rgb(')) return s.replace('rgb(', 'rgba(').replace(')', `,${a})`);
        if (s.startsWith('#')) {
          const h = s.slice(1);
          const nn = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
          const r = parseInt(nn.slice(0, 2), 16);
          const g = parseInt(nn.slice(2, 4), 16);
          const b = parseInt(nn.slice(4, 6), 16);
          return `rgba(${r},${g},${b},${a})`;
        }
        return s;
      };
      const paletteFromColor = (primary: string, label: string) => ({
        bg: '#fff',
        accent: primary,
        accentSoft: toRgba(primary, 0.14),
        border: toRgba(primary, 0.38),
        badgeBg: primary,
        primary,
        label,
        headerBg: toRgba(primary, 0.08),
      });
      const resolveForMachine = (m: any) => {
        const eff = m.calculatedEfficiency ?? m.efficiency ?? 0;
        const reason = m.reason || (m.machineStatus && m.machineStatus.reason) || 'ignored';
        const threshold = m.efficiencyThreshold || 95;
        return resolveStatus(eff, !!m.isOn, m.isStale, reason, threshold);
      };

      // Previous-shift handling. Opposite of today's shift — if current is A (day 8am-8pm),
      // previous = B (last night); if current is B, previous = A (today's earlier day shift).
      const currentShiftKey = ShiftService.getCurrentShift().shift; // 'A' | 'B'
      const prevShiftKey: 'A' | 'B' = currentShiftKey === 'A' ? 'B' : 'A';
      const prevShiftLabel = `Shift ${prevShiftKey}`;

      // When tvPrevShift is true, override each machine's efficiency/meters with
      // the stored shift-specific fields. Source from machinesWithData because
      // that's the enriched array that carries dayShiftEfficiency /
      // nightShiftEfficiency / meters12HourDay / meters12HourNight — the raw
      // `machines` list does not.
      // Per-shift picks and RPM are not stored anywhere — show 0 honestly.
      const sourceMachines: any[] = (machinesWithData && machinesWithData.length > 0)
        ? machinesWithData
        : (machines || []);
      const displayMachines: any[] = tvPrevShift
        ? sourceMachines.map((m: any) => {
            const shiftEff = prevShiftKey === 'A'
              ? (m.dayShiftEfficiency ?? 0)
              : (m.nightShiftEfficiency ?? 0);
            const shiftMeters = prevShiftKey === 'A'
              ? (m.meters12HourDay ?? 0)
              : (m.meters12HourNight ?? 0);
            return {
              ...m,
              efficiency: shiftEff,
              calculatedEfficiency: shiftEff,
              totalMeters: shiftMeters,
              isOn: shiftEff > 0,
              reason: 'ignored',
              isStale: false,
              d2: 0,
              rpm: 0,
            };
          })
        : sourceMachines;

      // Rebuild the grouped map from displayMachines so line averages reflect the
      // selected shift. Group by the same key used for the live view: line/group name.
      const displayGrouped: Record<string, any[]> = {};
      if (tvPrevShift) {
        const byId = new Map<number, any>(displayMachines.map(m => [m.id, m]));
        Object.entries(groupedMachines).forEach(([k, ms]) => {
          displayGrouped[k] = (ms as any[]).map(m => byId.get(m.id) || m);
        });
      }
      const groupedForTv = tvPrevShift ? displayGrouped : groupedMachines;

      const n = displayMachines.length || 1;
      const lineGroups = (Object.entries(groupedForTv) as [string, any[]][])
        .filter(([name, ms]) => ms.length > 0 && name !== 'Ungrouped Machines');
      const ungrouped = groupedForTv['Ungrouped Machines'] || [];
      const hasGroups = lineGroups.length > 0;

      // Compute KPI counts from displayMachines (so previous-shift toggle updates them)
      const tvStatusCounts = (() => {
        if (!tvPrevShift) return statusCounts;
        const ranges = [...config.efficiencyRanges].sort((a, b) => b.min - a.min);
        const [r0, r1, r2, r3] = ranges;
        let running = 0, lowEfficiency = 0, poorEfficiency = 0, problemEfficiency = 0;
        let totalEff = 0, effCount = 0, totalMtrs = 0;
        displayMachines.forEach(m => {
          const eff = m.efficiency || 0;
          if (r0 && eff >= r0.min) running++;
          else if (r1 && eff >= r1.min) lowEfficiency++;
          else if (r2 && eff >= r2.min) poorEfficiency++;
          else if (r3 && eff >= r3.min) problemEfficiency++;
          if (eff) { totalEff += eff; effCount++; }
          totalMtrs += m.totalMeters || 0;
        });
        return {
          running,
          lowEfficiency,
          poorEfficiency,
          problemEfficiency,
          fault: 0,
          offline: 0,
          manual: 0,
          avgEfficiency: effCount ? totalEff / effCount : 0,
          totalMeters: totalMtrs,
        };
      })();

      // For flat-mode fallback (when there are no groups), pick an appropriate rows x cols layout
      const layout = (() => {
        if (n <= 6) return { cols: 3, rows: 2 };
        if (n <= 12) return { cols: 4, rows: 3 };
        if (n <= 20) return { cols: 5, rows: 4 };
        if (n <= 24) return { cols: 6, rows: 4 };
        if (n <= 30) return { cols: 6, rows: 5 };
        if (n <= 40) return { cols: 8, rows: 5 };
        if (n <= 48) return { cols: 8, rows: 6 };
        if (n <= 60) return { cols: 10, rows: 6 };
        if (n <= 72) return { cols: 12, rows: 6 };
        if (n <= 84) return { cols: 12, rows: 7 };
        if (n <= 100) return { cols: 10, rows: 10 };
        return { cols: Math.ceil(Math.sqrt(n)), rows: Math.ceil(n / Math.ceil(Math.sqrt(n))) };
      })();
      const maxPerLine = hasGroups ? Math.max(...lineGroups.map(([, ms]) => ms.length)) : layout.cols;

      // Single machine card — all sizing via tokens in index.css
      const renderCard = (m: any) => {
        const eff = Math.round(m.efficiency || 0);
        const picks = m.d2 || 0;
        const isOn = !!m.isOn;
        const resolved = resolveForMachine(m);
        const palette = paletteFromColor(resolved.color, resolved.label);
        // Live "stopped for" timer — same stopStateFor logic as the grid tile (server
        // stop-start or client fallback). Live view only; previous-shift is a past snapshot.
        const live = !tvPrevShift;
        const { stopped, effStart, blinkCandidate } = stopStateFor(m, isOn);
        const showStopTimer = live && stopped && effStart != null;
        return (
          <StopBlinkDiv
            key={m.id}
            className="tv-card"
            style={{ borderColor: palette.border }}
            // Live view only: previous-shift cards are a past snapshot.
            candidate={live && blinkCandidate}
            startedAt={effStart}
          >
            <div
              className="tv-card__head"
              style={{ background: palette.headerBg, borderColor: palette.border }}
            >
              <span className="tv-card__num" style={{ color: palette.primary }}>#{m.number}</span>
              <span className="tv-card__badge" style={{ background: palette.badgeBg }}>
                <span className={`tv-card__dot ${isOn ? 'tv-card__dot--live' : ''}`} />
                {palette.label}
              </span>
            </div>
            {showStopTimer && <TvStoppedTimer startedAt={effStart} />}
            <div className="tv-card__body">
              <p className="tv-card__eff" style={{ color: palette.primary }}>
                {eff}<span>%</span>
              </p>
              <div className="tv-card__bar" style={{ background: palette.accentSoft }}>
                <div style={{ background: palette.accent, width: `${Math.max(0, Math.min(100, eff))}%` }} />
              </div>
              <div className="tv-card__foot">
                <span>
                  <span className="tv-card__foot-label">Picks</span>
                  <b>{picks.toLocaleString('en-IN')}</b>
                </span>
                <span>
                  <span className="tv-card__foot-label">Rpm</span>
                  <b>{m.rpm || 0}</b>
                </span>
              </div>
            </div>
          </StopBlinkDiv>
        );
      };

      return (
        <div className="tv-mode-root">
          {/* Banner */}
          <div className="tv-mode-banner">
            {/* Left: company identity */}
            <div className="tv-mode-banner__left">
              <div className="tv-mode-banner__logo">
                {((user as any)?.companyName || 'M').charAt(0).toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <p className="tv-mode-banner__title">{(user as any)?.companyName || 'Textile Monitoring'}</p>
                <div className="tv-mode-banner__meta">
                  {tvPrevShift ? (
                    <span className="tv-mode-pill tv-mode-pill--prev">
                      <span className="tv-mode-pill__dot" /> {prevShiftLabel}
                    </span>
                  ) : (
                    <span className="tv-mode-pill tv-mode-pill--live">
                      <span className="tv-mode-pill__dot" /> Live
                    </span>
                  )}
                  <TvClock />
                </div>
              </div>
            </div>

            {/* Middle: KPI chips */}
            <div className="tv-mode-kpis">
              <KpiChip icon={<Gauge />} label="Avg Eff" value={`${Math.round(tvStatusCounts.avgEfficiency)}%`} tone="emerald" />
              <KpiChip icon={<Package />} label="Total Mtrs" value={Math.round(tvStatusCounts.totalMeters).toLocaleString('en-IN')} tone="indigo" />
              <KpiChip icon={<PlayCircle />} label="Running" value={tvStatusCounts.running} tone="green" color={sortedRanges[0]?.color} />
              <KpiChip icon={<TrendingDown />} label="Low Eff" value={tvStatusCounts.lowEfficiency} tone="fuchsia" color={sortedRanges[1]?.color} />
              <KpiChip icon={<AlertTriangle />} label="Poor Eff" value={tvStatusCounts.poorEfficiency || 0} tone="yellow" color={sortedRanges[2]?.color} />
              <KpiChip icon={<AlertOctagon />} label="Problem" value={tvStatusCounts.problemEfficiency || 0} tone="orange" color={sortedRanges[3]?.color} />
              <KpiChip icon={<XCircle />} label="Fault" value={tvStatusCounts.fault} tone="red" color={config.specialStatuses.fault.color} />
              <KpiChip icon={<Hand />} label="Manual" value={tvStatusCounts.manual} tone="blue" color={config.specialStatuses.manual.color} />
              <KpiChip icon={<PowerOff />} label="Offline" value={tvStatusCounts.offline || 0} tone="slate" color={config.specialStatuses.offline.color} />
            </div>

            {/* Right: controls */}
            <div className="tv-mode-banner__right">
              <button
                type="button"
                onClick={() => setTvPrevShift(v => !v)}
                title={tvPrevShift ? `Viewing ${prevShiftLabel} — tap to go live` : `Show ${prevShiftLabel}`}
                className={`tv-btn ${tvPrevShift ? 'tv-btn--active' : ''}`}
              >
                <History />
                <span>{tvPrevShift ? prevShiftLabel : 'Prev Shift'}</span>
              </button>
              <button
                type="button"
                onClick={exitTvMode}
                title="Exit TV Mode (Esc)"
                className="tv-btn tv-btn--icon"
              >
                <XIcon />
              </button>
            </div>
          </div>

          {/* Machine grid — grouped by line */}
          <div className="tv-mode-scroll">
            {hasGroups ? (
              <>
                {lineGroups.map(([groupName, groupMs]) => {
                  const eff = groupMs.length ? Math.round(groupMs.reduce((s: number, m: any) => s + (m.efficiency || 0), 0) / groupMs.length) : 0;
                  const running = groupMs.filter((m: any) => m.isOn).length;
                  const totalPicks = groupMs.reduce((s: number, m: any) => s + (m.d2 || 0), 0);
                  const rangeMatch = sortedRanges.find(r => eff >= r.min) || sortedRanges[sortedRanges.length - 1];
                  const toneColor = rangeMatch?.color || runningColor;
                  return (
                    <div
                      key={groupName}
                      className="tv-line"
                      style={{ borderColor: toRgba(toneColor, 0.35), boxShadow: `0 1px 2px rgba(0,0,0,0.04), 0 4px 12px -4px ${toRgba(toneColor, 0.25)}` }}
                    >
                      <div
                        className="tv-line__label"
                        style={{ background: toRgba(toneColor, 0.08), borderRightColor: toRgba(toneColor, 0.35) }}
                      >
                        <span className="tv-line__name">{groupName}</span>
                        <span className="tv-line__count">
                          <span className="tv-line__count-dot" style={{ background: toneColor }} />
                          {running}/{groupMs.length}
                        </span>
                        <span className="tv-line__eff" style={{ color: toneColor }}>{eff}%</span>
                        <div className="tv-line__bar" style={{ background: toRgba(toneColor, 0.15) }}>
                          <div style={{ width: `${eff}%`, background: toneColor }} />
                        </div>
                        <span className="tv-line__picks">P {totalPicks.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="tv-line-grid" style={{ ['--tv-line-cols' as any]: maxPerLine }}>
                        {groupMs.map(renderCard)}
                      </div>
                    </div>
                  );
                })}
                {ungrouped.length > 0 && (
                  <div className="tv-line" style={{ borderColor: '#e2e8f0', flex: 'none' }}>
                    <div className="tv-line__label" style={{ background: '#f8fafc', borderRightColor: '#e2e8f0' }}>
                      <span className="tv-line__name">Ungrouped</span>
                    </div>
                    <div className="tv-line-grid" style={{ ['--tv-line-cols' as any]: maxPerLine }}>
                      {ungrouped.map(renderCard)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="tv-line-grid" style={{ ['--tv-line-cols' as any]: layout.cols, flex: 1 }}>
                {displayMachines.map(renderCard)}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="tv-mode-footer">
            <span>Press <kbd>Esc</kbd> to exit full-screen</span>
            <span>© {new Date().getFullYear()} VueNexa Technologies · {tvPrevShift ? `${prevShiftLabel} snapshot` : 'Live dashboard'}</span>
          </div>
        </div>
      );
    })(),
    document.body
  ) : null;

  return (
    <div className="min-h-screen relative transition-colors duration-300 font-['Inter',system-ui,-apple-system,sans-serif]">
      {tvOverlay}
      <div className="w-full px-2 sm:px-4 lg:px-6 py-6">
        {/* Top Command Ribbon - Status Bar */}
        <div className="mb-4 bg-white border border-slate-200 rounded-2xl px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex flex-col xl:flex-row xl:items-center justify-between gap-3 sm:gap-4 shadow-xl backdrop-blur-md">

          {/* Left Side: Total Meters + Status Counters - 2 per row on mobile, labels always visible */}
          <div className="grid grid-cols-2 sm:flex sm:flex-row sm:flex-wrap gap-2 sm:gap-3 w-full xl:w-auto sm:items-center">
            {/* Total Meters - Sum of all machines */}
            <div className="col-span-2 w-full sm:w-auto flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/30 min-w-0 shrink-0">
              <BarChart3 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-emerald-700 whitespace-nowrap">Total Mtrs</span>
              <span className="text-sm sm:text-base font-black font-mono text-emerald-900 truncate" title={statusCounts.totalMeters.toFixed(0)}>
                {Number(statusCounts.totalMeters).toLocaleString('en-IN', {
                  maximumFractionDigits: 0
                })}
              </span>
            </div>

            {/* Running - Good Efficiency (Green) */}
            <button type="button" onClick={() => setSelectedStatusCategory('running')} disabled={statusCounts.running === 0} title="View machines" className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-green-500/10 border border-green-500/20 min-w-0 shrink-0 cursor-pointer transition hover:brightness-95 active:scale-[0.97] disabled:cursor-default disabled:hover:brightness-100 disabled:opacity-60">
              <PlayCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-green-700">Running</span>
              <span className="text-sm font-black text-green-900">{statusCounts.running}</span>
            </button>

            {/* Low Efficiency */}
            <button type="button" onClick={() => setSelectedStatusCategory('lowEfficiency')} disabled={statusCounts.lowEfficiency === 0} title="View machines" className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/20 min-w-0 shrink-0 cursor-pointer transition hover:brightness-95 active:scale-[0.97] disabled:cursor-default disabled:hover:brightness-100 disabled:opacity-60">
              <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-fuchsia-500 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-fuchsia-700">Low Eff</span>
              <span className="text-sm font-black text-fuchsia-900">{statusCounts.lowEfficiency}</span>
            </button>

            {/* Poor Efficiency */}
            <button type="button" onClick={() => setSelectedStatusCategory('poorEfficiency')} disabled={(statusCounts.poorEfficiency || 0) === 0} title="View machines" className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-yellow-500/10 border border-yellow-500/20 min-w-0 shrink-0 cursor-pointer transition hover:brightness-95 active:scale-[0.97] disabled:cursor-default disabled:hover:brightness-100 disabled:opacity-60">
              <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-600 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-yellow-700">Poor Eff</span>
              <span className="text-sm font-black text-yellow-900">{statusCounts.poorEfficiency || 0}</span>
            </button>

            {/* Problem */}
            <button type="button" onClick={() => setSelectedStatusCategory('problemEfficiency')} disabled={(statusCounts.problemEfficiency || 0) === 0} title="View machines" className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-orange-500/10 border border-orange-500/20 min-w-0 shrink-0 cursor-pointer transition hover:brightness-95 active:scale-[0.97] disabled:cursor-default disabled:hover:brightness-100 disabled:opacity-60">
              <AlertOctagon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-orange-700">Problem</span>
              <span className="text-sm font-black text-orange-900">{statusCounts.problemEfficiency || 0}</span>
            </button>

            {/* Fault */}
            <button type="button" onClick={() => setSelectedStatusCategory('fault')} disabled={statusCounts.fault === 0} title="View machines" className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-red-500/10 border border-red-500/20 min-w-0 shrink-0 cursor-pointer transition hover:brightness-95 active:scale-[0.97] disabled:cursor-default disabled:hover:brightness-100 disabled:opacity-60">
              <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-red-700">Fault</span>
              <span className="text-sm font-black text-red-900">{statusCounts.fault}</span>
            </button>

            {/* Manual */}
            <button type="button" onClick={() => setSelectedStatusCategory('manual')} disabled={statusCounts.manual === 0} title="View machines" className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-blue-500/10 border border-blue-500/20 min-w-0 shrink-0 cursor-pointer transition hover:brightness-95 active:scale-[0.97] disabled:cursor-default disabled:hover:brightness-100 disabled:opacity-60">
              <Hand className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-blue-700">Manual</span>
              <span className="text-sm font-black text-blue-900">{statusCounts.manual}</span>
            </button>

            {/* Offline */}
            <button type="button" onClick={() => setSelectedStatusCategory('offline')} disabled={statusCounts.offline === 0} title="View machines" className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-slate-500/10 border border-slate-500/20 min-w-0 shrink-0 cursor-pointer transition hover:brightness-95 active:scale-[0.97] disabled:cursor-default disabled:hover:brightness-100 disabled:opacity-60">
              <PowerOff className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-slate-600">Offline</span>
              <span className="text-sm font-black text-slate-900">{statusCounts.offline}</span>
            </button>

            {/* Avg Efficiency */}
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 min-w-0 shrink-0">
              <Gauge className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-500 shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-cyan-700">Avg Eff</span>
              <span className="text-sm font-black text-cyan-900">{statusCounts.avgEfficiency.toFixed(0)}%</span>
            </div>
          </div>

          {/* Right Side: Controls with Premium Glass UI */}
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto justify-end">
            {/* Time Range Selector - Full width on mobile, auto on desktop */}
            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner w-full sm:w-auto">
              <button
                onClick={() => setStatusRange('today')}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-xs font-black rounded-lg transition-all ${statusRange === 'today' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                <Clock className="w-4 h-4" />
                <span className="uppercase tracking-widest">Today</span>
              </button>
              <button
                onClick={() => setStatusRange('lastDay')}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-xs font-black rounded-lg transition-all ${statusRange === 'lastDay' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                <Activity className="w-4 h-4" />
                <span className="uppercase tracking-widest">24H</span>
              </button>
            </div>

            {/* Actions Row: Export + TV Mode */}
            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
              {/* Export Button - Expanded for touch on mobile */}
              <button
                onClick={() => setShowExportAllModal(true)}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl bg-slate-900 text-white hover:bg-black transition-all shadow-lg active:scale-95"
              >
                <Download className="w-4 h-4 text-green-400" />
                <span>Export All</span>
              </button>

              {/* Stoppage Report - all machines' stops as Excel/PDF (shared component) */}
              <StoppageReportExport
                triggerClassName="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl bg-red-600 text-white hover:bg-red-700 transition-all shadow-lg active:scale-95"
                triggerLabel="Stoppage"
              />

              {/* TV Mode - full-screen kiosk for smart TV */}
              <button
                onClick={enterTvMode}
                title="Open TV Mode (fullscreen kiosk for smart TV)"
                className="flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg active:scale-95 flex-shrink-0"
              >
                <Maximize2 className="w-4 h-4" />
                <span>TV Mode</span>
              </button>
            </div>
          </div>
        </div>

        <div className="">
          {/* Floating Action Buttons */}
          <div className="fixed top-6 right-6 z-[1002] flex flex-col gap-3">
            {/* Make Group Button - only show if there are ungrouped machines */}
            {ungroupedMachines.length > 0 && (
              <button
                type="button"
                onClick={openMakeGroupModal}
                className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-600 text-white shadow-xl hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
                title="Make Group"
              >
                <Users className="h-7 w-7" />
              </button>
            )}
            {/* Add Machine Button */}
            {/* <button
              type="button"
              onClick={openModal}
              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
              title="Add Machine"
            >
              <Plus className="h-6 w-6" />
            </button> */}
          </div>

          {/* WebSocket Connection Status */}
          {/* <div className="absolute -top-6 left-0 z-30">
              <div
                  className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${connectionInfo.statusColor}`}>
                <div
                    className={`w-2 h-2 rounded-full mr-2 ${connectionInfo.isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                    }`}></div>
                {connectionInfo.statusText}
              </div>
            </div> */}

          {/* Selected machines actions - only show when machines are selected */}
          {selectedMachines.length > 0 && (
            <div className="flex items-center justify-center space-x-3 mb-4">
              <span className="text-sm text-gray-600">
                {t('machines.selected', { count: selectedMachines.length })}
              </span>
              <button
                type="button"
                onClick={openGroupingModal}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t('machines.group')}
              </button>
              <button
                type="button"
                onClick={handleRemoveFromGroup}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t('machines.removeFromGroup')}
              </button>
            </div>
          )}

          {/* Machines Table */}
          <div className="shadow overflow-hidden sm:rounded-md">
            {(isLoading || needsCompany) ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-500">{needsCompany ? 'Loading company…' : 'Loading machines...'}</span>
                </div>
              </div>
            ) : machines.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">{t('machines.noMachines')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {!groupByType && (
                  <div className="bg-white px-6 py-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedMachines.length === machines.length && machines.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm font-medium text-gray-700">
                        {t('machines.selectAll', { count: machines.length })}
                      </label>
                    </div>
                  </div>
                )}


                <div className="space-y-4">
                  {/* Mobile: Horizontal Group Tab Bar */}
                  {(Object.keys(groupedOnly).length > 0 || ungroupedMachines.length > 0) && (
                    <div className="sm:hidden px-2">
                      {Object.keys(groupedOnly).length > 0 && (
                      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-2 border border-gray-100 shadow-sm mb-4">
                        <div className="flex overflow-x-auto gap-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                          {Object.entries(groupedOnly).map(([groupName, groupMachines]) => {
                            const isActive = mobileSelectedGroup === groupName;
                            const eff = calculateGroupEfficiency(groupMachines as Machine[]);
                            const issues = getProblematicMachines(groupMachines as Machine[]).length;
                            const effColor = eff >= 95 ? '#16a34a' : eff >= 90 ? '#a855f7' : eff >= 60 ? '#eab308' : '#f97316';
                            return (
                              <button
                                key={groupName}
                                onClick={() => setMobileSelectedGroup(groupName)}
                                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all duration-300 ${
                                  isActive
                                    ? 'bg-slate-800 text-white shadow-lg shadow-slate-800/25 scale-[1.02]'
                                    : 'bg-gray-50 text-slate-600 hover:bg-gray-100 active:scale-95'
                                }`}
                              >
                                <span className="whitespace-nowrap">{groupName}</span>
                                <span
                                  className={`text-[10px] font-black font-mono px-2 py-0.5 rounded-lg ${
                                    isActive ? 'bg-white/20 text-white/90' : ''
                                  }`}
                                  style={!isActive ? { color: effColor, backgroundColor: `${effColor}12` } : {}}
                                >
                                  {eff.toFixed(0)}%
                                </span>
                              </button>
                            );
                          })}
                          {ungroupedMachines.length > 0 && (
                            <button
                              onClick={() => setMobileSelectedGroup('__ungrouped__')}
                              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all duration-300 ${
                                mobileSelectedGroup === '__ungrouped__'
                                  ? 'bg-slate-800 text-white shadow-lg shadow-slate-800/25 scale-[1.02]'
                                  : 'bg-gray-50 text-slate-600 hover:bg-gray-100 active:scale-95'
                              }`}
                            >
                              <span className="whitespace-nowrap">Ungrouped</span>
                              <span className={`text-[10px] font-black font-mono px-2 py-0.5 rounded-lg ${
                                mobileSelectedGroup === '__ungrouped__' ? 'bg-white/20 text-white/90' : 'bg-slate-100 text-slate-500'
                              }`}>
                                {ungroupedMachines.length}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Mobile: Selected group machines */}
                      {mobileSelectedGroup && mobileSelectedGroup !== '__ungrouped__' && groupedOnly[mobileSelectedGroup] && (
                        <div>
                          <div className="grid grid-cols-2 gap-2">
                            {(groupedOnly[mobileSelectedGroup] as Machine[]).map(machine => (
                              <MachineTile key={machine.id} machine={machine} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Mobile: Ungrouped machines — same compact tiles as groups */}
                      {mobileSelectedGroup === '__ungrouped__' && ungroupedMachines.length > 0 && (
                        <div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {ungroupedMachines
                              .sort((a: Machine, b: Machine) => (parseInt(a.number) || 0) - (parseInt(b.number) || 0))
                              .map((machine: Machine) => (
                                <MachineTile key={machine.id} machine={machine} />
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Desktop: Grouped Machines with Drag and Drop */}
                  <div className="hidden sm:block">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={Object.keys(groupedOnly)}
                        strategy={verticalListSortingStrategy}
                      >
                        {Object.entries(groupedOnly).map(([groupName, groupMachines]) => (
                          <SortableGroup
                            key={groupName}
                            groupName={groupName}
                            groupMachines={groupMachines as Machine[]}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>

                    {/* Ungrouped Machines Section — same compact tiles as groups */}
                    {ungroupedMachines.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-8 2xl:grid-cols-10 gap-3 sm:gap-4 mt-4">
                        {ungroupedMachines
                          .sort((a: Machine, b: Machine) => {
                            const numA = parseInt(a.number) || 0;
                            const numB = parseInt(b.number) || 0;
                            return numA - numB;
                          })
                          .map((machine: Machine) => (
                            <MachineTile key={machine.id} machine={machine} />
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div className="mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('machines.addNewMachine')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('machines.bulkCreateDescription')}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {showCompanyColumn && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                    <CompanySelect value={companyPickerTenantId} onChange={setCompanyPickerTenantId} />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2">{t('machines.machineFrom')}</label>
                    <input
                      {...register('from', {
                        required: t('machines.machineFromRequired'),
                        min: { value: 1, message: t('machines.machineFromMin') }
                      })}
                      type="number"
                      min="1"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('machines.machineFromPlaceholder')}
                    />
                    {errors.from && (
                      <p className="mt-1 text-sm text-red-600">{errors.from.message}</p>
                    )}
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2">{t('machines.machineTo')}</label>
                    <input
                      {...register('to', {
                        required: t('machines.machineToRequired'),
                        min: { value: 1, message: t('machines.machineToMin') }
                      })}
                      type="number"
                      min="1"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('machines.machineToPlaceholder')}
                    />
                    {errors.to && (
                      <p className="mt-1 text-sm text-red-600">{errors.to.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('machines.machineType') || 'Machine Type'}
                  </label>
                  <select
                    {...register('machineType', { required: 'Machine type is required' })}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="water_jacket">Water Jacket</option>
                    <option value="rapier_jacket">Rapier Jacket</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || createBulkMachinesMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isPending || createBulkMachinesMutation.isPending ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingMachine && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div className="mx-auto p-5 border w-96 max-w-[calc(100%-2rem)] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Machine</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete Machine #{deletingMachine.number}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingMachine(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Machine Grouping Modal */}
      {showGroupingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div className="mx-auto p-5 border w-96 max-w-[calc(100%-2rem)] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Create Group
                </h3>
                <button
                  onClick={closeGroupingModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Selected {selectedMachines.length} machine(s)
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter group name (e.g., Line 1, Group A)"
                    autoFocus
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeGroupingModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateGroup}
                    disabled={createGroupAndAssignMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createGroupAndAssignMutation.isPending ? 'Creating...' : 'Create Group'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Production Modal */}
      {showProductionModal && selectedMachineForProduction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div
            className="mx-auto p-0 border w-11/12 max-w-2xl shadow-lg rounded-lg bg-white border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="bg-gray-50 border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-md">
                    <Play className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Start Production
                    </h3>
                    <p className="text-gray-600 text-sm">Machine #{selectedMachineForProduction.number}</p>
                  </div>
                </div>
                <button
                  onClick={closeProductionModal}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-md hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">

              <form onSubmit={handleSubmitProduction(onSubmitProduction)} className="space-y-6">
                {/* Machine Info (Readonly) */}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings className="h-4 w-4 text-gray-600" />
                    <h4 className="text-sm font-medium text-gray-700">Machine Information</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Machine</div>
                      <p className="text-sm font-semibold text-gray-900">#{selectedMachineForProduction.number}</p>
                    </div>
                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Current Pick Value</div>
                      <p className="text-sm font-semibold text-gray-900">{selectedMachineForProduction.pickValue}</p>
                    </div>
                  </div>
                </div>

                {/* Production Form */}
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Beam Selection
                      </label>
                      <select
                        {...registerProduction('beamId', { required: 'Beam is required' })}
                        onChange={(e) => {
                          const beamId = parseInt(e.target.value);
                          handleBeamChange(beamId);
                          registerProduction('beamId').onChange(e);
                        }}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={0}>Select Beam</option>
                        {beams.length === 0 ? (
                          <option value={0} disabled>No beams available (permission required)</option>
                        ) : (
                          beams.map((beam: Beam) => (
                            <option key={beam.id} value={beam.id}>
                              {beam.number} - {beam.metersRemaining?.toFixed(0) || 0}m remaining
                              {beam.weight && ` (${beam.weight}kg)`}
                            </option>
                          ))
                        )}
                      </select>
                      {beams.length === 0 && !hasModulePermission('beams') && (
                        <p className="mt-1 text-xs text-gray-500">
                          Beams module permission required to start production
                        </p>
                      )}
                      {productionErrors.beamId && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <span className="text-red-500">⚠</span>
                          {productionErrors.beamId.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Quality Selection
                      </label>
                      <select
                        {...registerProduction('qualityId', { required: 'Quality is required' })}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={0}>Select Quality</option>
                        {qualities.length === 0 ? (
                          <option value={0} disabled>No qualities available (permission required)</option>
                        ) : (
                          qualities.map((quality: Quality) => (
                            <option key={quality.id} value={quality.id}>
                              {quality.name}
                            </option>
                          ))
                        )}
                      </select>
                      {qualities.length === 0 && !hasModulePermission('qualities') && (
                        <p className="mt-1 text-xs text-gray-500">
                          Qualities module permission required to start production
                        </p>
                      )}
                      {productionErrors.qualityId && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <span className="text-red-500">⚠</span>
                          {productionErrors.qualityId.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Employee Assignment
                      </label>
                      <select
                        {...registerProduction('employeeId', { required: 'Employee is required' })}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={0}>Select Employee</option>
                        {employees.length === 0 ? (
                          <option value={0} disabled>No employees available (permission required)</option>
                        ) : (
                          employees.map((employee: Employee) => (
                            <option key={employee.id} value={employee.id}>
                              {employee.user.name}
                            </option>
                          ))
                        )}
                      </select>
                      {employees.length === 0 && !hasModulePermission('employees') && (
                        <p className="mt-1 text-xs text-gray-500">
                          Employees module permission required to start production
                        </p>
                      )}
                      {productionErrors.employeeId && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <span className="text-red-500">⚠</span>
                          {productionErrors.employeeId.message}
                        </p>
                      )}

                      {/* Auto-selected shift employee info */}
                      {selectedMachineForProduction?.group && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex items-center space-x-2 text-sm">
                            <Clock className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-blue-800">Current Shift</span>
                          </div>
                          <div className="mt-1 text-sm text-blue-700">
                            {(() => {
                              const currentShift = ShiftService.getCurrentShift();
                              const shiftEmployee = ShiftService.getActiveShiftEmployee(
                                selectedMachineForProduction.group,
                                currentShift.shift
                              );
                              return shiftEmployee ? (
                                <span>
                                  {ShiftService.getShiftIcon(currentShift.shift)} {ShiftService.getShiftDisplayName(currentShift.shift)}: {shiftEmployee.user.name}
                                </span>
                              ) : (
                                <span className="text-orange-600">
                                  {ShiftService.getShiftIcon(currentShift.shift)} {ShiftService.getShiftDisplayName(currentShift.shift)}: No employee assigned
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Meters to Produce
                      <span className="text-xs text-gray-500 ml-2">(Auto-calculated)</span>
                    </label>
                    <input
                      {...registerProduction('meters')}
                      type="number"
                      step="0.01"
                      min="0"
                      readOnly
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-700 cursor-not-allowed"
                      placeholder="0.00"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Automatically set from beam remaining meters
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Pick Value
                    </label>
                    <input
                      {...registerProduction('pickValue', {
                        required: 'Pick value is required',
                        min: { value: 0, message: 'Pick value must be 0 or greater' }
                      })}
                      type="number"
                      step="0.01"
                      min="0"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                    {productionErrors.pickValue && (
                      <p className="mt-1 text-sm text-red-600">
                        {productionErrors.pickValue.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Error Rate (%)
                  </label>
                  <input
                    {...registerProduction('errorRate', {
                      required: 'Error rate is required',
                      min: { value: 0, message: 'Error rate must be 0 or greater' },
                      max: { value: 100, message: 'Error rate must be 100 or less' }
                    })}
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="5.0"
                  />
                  {productionErrors.errorRate && (
                    <p className="mt-1 text-sm text-red-600">
                      {productionErrors.errorRate.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Percentage of expected waste/error in production
                  </p>
                </div>



                {/* Beam Details */}
                {(() => {
                  const selectedBeamId = watchProduction('beamId');
                  console.log('Selected Beam ID:', selectedBeamId);
                  console.log('Available Beams:', beams);

                  if (selectedBeamId && selectedBeamId > 0) {
                    // Convert to number if it's a string
                    const beamId = typeof selectedBeamId === 'string' ? parseInt(selectedBeamId) : selectedBeamId;
                    const selectedBeam = beams.find((b: any) => b.id === beamId);
                    console.log('Beam ID (converted):', beamId);
                    console.log('Found Beam:', selectedBeam);

                    return (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Package className="h-4 w-4 text-gray-600" />
                          <h4 className="text-sm font-medium text-gray-700">Beam Details</h4>
                        </div>

                        {selectedBeam ? (
                          <div className="space-y-3">
                            {/* Basic Info Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white rounded-md p-3 border border-gray-200">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Beam Number</div>
                                <p className="text-sm font-semibold text-gray-900">{selectedBeam.number}</p>
                              </div>

                              <div className="bg-white rounded-md p-3 border border-gray-200">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Weight</div>
                                <p className="text-sm font-semibold text-gray-900">{selectedBeam.weight || 'N/A'} kg</p>
                              </div>
                            </div>

                            {/* Meters Progress */}
                            <div className="bg-white rounded-md p-3 border border-gray-200">
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Meters Progress</div>

                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">Total Meters</span>
                                  <span className="text-sm font-medium text-gray-900">{selectedBeam.totalMeters?.toFixed(0) || 0}m</span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">Used Meters</span>
                                  <span className="text-sm font-medium text-gray-900">{selectedBeam.metersUsed?.toFixed(0) || 0}m</span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">Remaining Meters</span>
                                  <span className="text-sm font-medium text-gray-900">{selectedBeam.metersRemaining?.toFixed(0) || 0}m</span>
                                </div>

                                {/* Progress Bar */}
                                <div className="mt-2">
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>Progress</span>
                                    <span>{((selectedBeam.metersUsed || 0) / (selectedBeam.totalMeters || 1) * 100).toFixed(1)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className="bg-blue-500 h-1.5 rounded-full"
                                      style={{
                                        width: `${((selectedBeam.metersUsed || 0) / (selectedBeam.totalMeters || 1) * 100)}%`
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Panno Info */}
                            {selectedBeam.panno && (
                              <div className="bg-white rounded-md p-3 border border-gray-200">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Panno</div>
                                <p className="text-sm font-semibold text-gray-900">{selectedBeam.panno}</p>
                              </div>
                            )}

                            {/* Yarns Used */}
                            {selectedBeam.yarns && selectedBeam.yarns.length > 0 && (
                              <div className="bg-white rounded-md p-3 border border-gray-200">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Yarns Used</div>
                                <div className="space-y-1">
                                  {selectedBeam.yarns.map((yarn: any, index: number) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-md p-2">
                                      <span className="text-sm text-gray-700">{yarn.name}</span>
                                      <span className="text-sm font-medium text-gray-900">{yarn.BeamYarn?.meters || 0}m</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-gray-500 text-sm">Beam not found</p>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeProductionModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={startProductionMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    style={{ display: 'none' }}
                  >
                    {startProductionMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Starting...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        Start Production
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Attach Beam Modal */}
      {showBeamModal && selectedMachineForBeam && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div className="mx-auto p-5 border w-96 max-w-[calc(100%-2rem)] shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Attach Beam - Machine #{selectedMachineForBeam.number}
                </h3>
                <button
                  onClick={() => {
                    setShowBeamModal(false);
                    setSelectedMachineForBeam(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beam Number *</label>
                  <input
                    type="text"
                    value={beamForm.beamNumber}
                    onChange={(e) => setBeamForm(prev => ({ ...prev, beamNumber: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. B-101"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beam Name</label>
                  <input
                    type="text"
                    value={beamForm.beamName}
                    onChange={(e) => setBeamForm(prev => ({ ...prev, beamName: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional display name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Meters *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={beamForm.beamTotalMeters}
                    onChange={(e) => setBeamForm(prev => ({ ...prev, beamTotalMeters: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Total meters on beam"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shortage % (wastage)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={beamForm.shortagePercentage}
                    onChange={(e) => setBeamForm(prev => ({ ...prev, shortagePercentage: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Effective meters: {beamForm.beamTotalMeters ? (parseFloat(beamForm.beamTotalMeters) * (1 - (parseFloat(beamForm.shortagePercentage) || 0) / 100)).toFixed(1) : '0'}m
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowBeamModal(false);
                    setSelectedMachineForBeam(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAttachBeam}
                  disabled={attachBeamMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {attachBeamMutation.isPending ? 'Attaching...' : 'Attach Beam'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Machine Settings Modal */}
      {showMachineSettingsModal && selectedMachineForSettings && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div className="mx-auto p-5 border w-96 max-w-[calc(100%-2rem)] shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Machine Settings - #{selectedMachineForSettings.number}
                </h3>
                <button
                  onClick={closeSettingsModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitSettings(onSubmitSettings)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pick Value</label>
                  <input
                    {...registerSettings('pickValue', {
                      required: 'Pick value is required',
                      min: { value: 0, message: 'Pick value must be 0 or greater' }
                    })}
                    type="number"
                    step="0.01"
                    min="0"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {settingsErrors.pickValue && (
                    <p className="mt-1 text-sm text-red-600">{settingsErrors.pickValue.message}</p>
                  )}
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Efficiency Threshold (%)
                  </label>
                  <input
                    {...registerSettings('efficiencyThreshold', {
                      required: 'Efficiency threshold is required',
                      min: { value: 0, message: 'Threshold must be 0 or greater' },
                      max: { value: 100, message: 'Threshold must be 100 or less' }
                    })}
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="95"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Machine will be highlighted if efficiency falls below this threshold
                  </p>
                  {settingsErrors.efficiencyThreshold && (
                    <p className="mt-1 text-sm text-red-600">{settingsErrors.efficiencyThreshold.message}</p>
                  )}
                </div>


                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeSettingsModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMachineSettingsMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMachineSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Pick Edit Modal */}
      {selectedMachineForPickEdit && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1010] p-4">
          <div className="mx-auto p-5 border w-96 max-w-[calc(100%-2rem)] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit Pick Value - Machine #{selectedMachineForPickEdit.number}
                </h3>
                <button
                  onClick={() => setSelectedMachineForPickEdit(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitSettings((data) => onSubmitPickEdit({ pickValue: data.pickValue }))} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pick Value</label>
                  <input
                    {...registerSettings('pickValue', {
                      required: 'Pick value is required',
                      min: { value: 0, message: 'Pick value must be 0 or greater' }
                    })}
                    type="number"
                    step="0.01"
                    min="0"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {settingsErrors.pickValue && (
                    <p className="mt-1 text-sm text-red-600">{settingsErrors.pickValue.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setSelectedMachineForPickEdit(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMachineSettingsMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMachineSettingsMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Efficiency Threshold Edit Modal */}
      {selectedMachineForEfficiencyEdit && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1010] p-4">
          <div className="mx-auto p-5 border w-96 max-w-[calc(100%-2rem)] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit Efficiency Threshold - Machine #{selectedMachineForEfficiencyEdit.number}
                </h3>
                <button
                  onClick={() => setSelectedMachineForEfficiencyEdit(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitSettings((data) => onSubmitEfficiencyEdit({ efficiencyThreshold: data.efficiencyThreshold }))} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Efficiency Threshold (%)
                  </label>
                  <input
                    {...registerSettings('efficiencyThreshold', {
                      required: 'Efficiency threshold is required',
                      min: { value: 0, message: 'Threshold must be 0 or greater' },
                      max: { value: 100, message: 'Threshold must be 100 or less' }
                    })}
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="95"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Machine will be highlighted if efficiency falls below this threshold
                  </p>
                  {settingsErrors.efficiencyThreshold && (
                    <p className="mt-1 text-sm text-red-600">{settingsErrors.efficiencyThreshold.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setSelectedMachineForEfficiencyEdit(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMachineSettingsMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMachineSettingsMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Machine Logs Modal */}
      {showLogsModal && selectedMachineForLogs && (
        <MachineLogs
          machineId={selectedMachineForLogs.id}
          machineNumber={selectedMachineForLogs.number}
          onClose={() => {
            setShowLogsModal(false);
            setSelectedMachineForLogs(null);
          }}
        />
      )}

      {/* Make Group Modal */}
      {showMakeGroupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div className="mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Create Machine Group
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Select machines and create a new group
                  </p>
                </div>
                <button
                  onClick={closeMakeGroupModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Group Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Group Name *
                    </label>
                    <input
                      type="text"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Line 1, Group A"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={groupDescription}
                      onChange={(e) => setGroupDescription(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description"
                    />
                  </div>
                </div>

                {/* Selection Method */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <button
                      type="button"
                      onClick={() => setUseRangeSelection(false)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${!useRangeSelection
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                      <Users className="h-4 w-4 mr-2 inline" />
                      Select Individual Machines
                    </button>
                    <button
                      type="button"
                      onClick={() => setUseRangeSelection(true)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${useRangeSelection
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                      <Hash className="h-4 w-4 mr-2 inline" />
                      Select by Range
                    </button>
                  </div>

                  {!useRangeSelection ? (
                    /* Individual Machine Selection */
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700">
                          Available Ungrouped Machines ({ungroupedMachines.length})
                        </h4>
                        <button
                          type="button"
                          onClick={handleSelectAllUngrouped}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {selectedUngroupedMachines.length === ungroupedMachines.length ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-4">
                          {ungroupedMachines.map((machine: Machine) => (
                            <label
                              key={machine.id}
                              className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors duration-200 ${selectedUngroupedMachines.includes(machine.id)
                                ? 'bg-blue-50 border-blue-300'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedUngroupedMachines.includes(machine.id)}
                                onChange={() => handleUngroupedMachineSelect(machine.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm font-medium text-gray-900">
                                #{machine.number}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Range Selection */
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700">
                        Select Machines by Range
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            From Machine #
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={rangeFrom}
                            onChange={(e) => setRangeFrom(parseInt(e.target.value) || 1)}
                            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            To Machine #
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={rangeTo}
                            onChange={(e) => setRangeTo(parseInt(e.target.value) || 1)}
                            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRangeSelection}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Select Range
                      </button>
                    </div>
                  )}

                  {/* Selected Machines Summary */}
                  {selectedUngroupedMachines.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>{selectedUngroupedMachines.length}</strong> machine(s) selected for grouping
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedUngroupedMachines.map(machineId => {
                          const machine = ungroupedMachines.find((m: Machine) => m.id === machineId);
                          return machine ? (
                            <span key={machineId} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              #{machine.number}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeMakeGroupModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateGroupFromUngrouped}
                    disabled={createGroupAndAssignMutation.isPending || selectedUngroupedMachines.length === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createGroupAndAssignMutation.isPending ? 'Creating...' : 'Create Group'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shift Assignment Modal */}
      {/* Meter Entry Modal */}
      {showMeterEntryModal && selectedMachineForMeterEntry && selectedMachineForMeterEntry.productions && selectedMachineForMeterEntry.productions.length > 0 && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1004] p-4">
          <div className="mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-medium text-gray-900">Add Meter Entry</h3>
              </div>
              <button
                onClick={() => {
                  setShowMeterEntryModal(false);
                  setSelectedMachineForMeterEntry(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {(() => {
              const production = selectedMachineForMeterEntry.productions[0];
              const { getCurrentShift } = ShiftService;
              const currentShift = getCurrentShift();

              return (
                <form onSubmit={handleSubmitMeterEntry} className="space-y-4">
                  {/* Production Info */}
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <div className="text-xs font-medium text-purple-800 mb-2">Production Details</div>
                    <div className="text-sm text-purple-700 space-y-1">
                      <div><span className="font-semibold">Machine:</span> #{selectedMachineForMeterEntry.number}</div>
                      <div><span className="font-semibold">Beam:</span> {production.beam?.number || 'N/A'}</div>
                      <div><span className="font-semibold">Quality:</span> {production.quality?.name || 'N/A'}</div>
                      <div><span className="font-semibold">Employee:</span> {production.employee?.user?.name || 'N/A'}</div>
                      <div className="pt-2 border-t border-purple-300">
                        <span className="font-semibold">Progress:</span> {(production.metersProduced || 0).toFixed(0)}/{(production.meters || 0).toFixed(0)}m
                      </div>
                    </div>
                  </div>

                  {/* Meter Entry Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meters Produced *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={meterEntryForm.meters}
                        onChange={(e) => setMeterEntryForm({ ...meterEntryForm, meters: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Enter meters produced"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Shift *</label>
                      <select
                        value={meterEntryForm.shiftType}
                        onChange={(e) => setMeterEntryForm({ ...meterEntryForm, shiftType: e.target.value as 'A' | 'B' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        required
                      >
                        <option value="A">☀️ Shift A (8 AM - 8 PM)</option>
                        <option value="B">🌙 Shift B (8 PM - 8 AM)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date *</label>
                      <input
                        type="date"
                        value={meterEntryForm.entryDate}
                        onChange={(e) => setMeterEntryForm({ ...meterEntryForm, entryDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                      <textarea
                        value={meterEntryForm.notes}
                        onChange={(e) => setMeterEntryForm({ ...meterEntryForm, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        rows={3}
                        placeholder="Additional notes about this meter entry"
                      />
                    </div>
                  </div>

                  {/* Current Shift Info */}
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="text-xs font-medium text-gray-700 mb-1">Current Shift</div>
                    <div className="text-sm text-gray-600">
                      {ShiftService.getShiftIcon(currentShift.shift)} {ShiftService.getShiftDisplayName(currentShift.shift)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {currentShift.startTime} - {currentShift.endTime}
                    </div>
                  </div>

                  {/* Quick Link to Meter Entries Page */}
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-center">
                    <p className="text-sm text-blue-800 mb-2">
                      For detailed meter management, visit the Meter Entries page
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMeterEntryModal(false);
                        window.location.href = '/meter-entries';
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Go to Meter Entries
                    </button>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => {
                        setShowMeterEntryModal(false);
                        setSelectedMachineForMeterEntry(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={addMeterEntryMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addMeterEntryMutation.isPending ? 'Adding...' : 'Add Meter Entry'}
                    </button>
                  </div>
                </form>
              );
            })()}
          </div>
        </div>
      )}

      <ShiftAssignmentModal
        isOpen={showShiftAssignmentModal}
        onClose={() => {
          setShowShiftAssignmentModal(false);
          setSelectedGroupForShiftAssignment(null);
        }}
        group={selectedGroupForShiftAssignment}
        employees={employeesResponse?.employees || []}
      />

      {/* Stop Events Modal — portaled to <body> and stacked above everything else
          (Machine Card modal z-1005, TV overlay .tv-mode-root z-99999), so tapping
          STOPS inside an open machine card always shows the list on top, whatever
          stacking context the page sits in. */}
      {showStopEventsModal && selectedMachineForStopEvents && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 100000 }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  Stop Events - Machine #{selectedMachineForStopEvents.number}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExportMachineStoppage('xlsx')}
                    disabled={isExportingStoppage}
                    title="Download this machine's stoppage report as Excel"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" /> Excel
                  </button>
                  <button
                    onClick={() => handleExportMachineStoppage('pdf')}
                    disabled={isExportingStoppage}
                    title="Download this machine's stoppage report as PDF"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" /> PDF
                  </button>
                  <button
                    onClick={() => {
                      setShowStopEventsModal(false);
                      setSelectedMachineForStopEvents(null);
                      setStopEvents([]);
                      setStopEventsData(null);
                      setEditingNoteIdx(null);
                      setNoteForm({ reason: '', note: '' });
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              {stopEventsData && (() => {
                // Helper function to determine shift for a timestamp (IST)
                const getShiftForTimestamp = (timestamp: string): 'Day' | 'Night' => {
                  if (!timestamp) return 'Day';
                  try {
                    // Parse timestamp in IST timezone
                    const date = dayjs.tz(timestamp, 'Asia/Kolkata');
                    const hour = date.hour();
                    // Day shift: 8 AM (8) to 7 PM (19) IST
                    // Night shift: 7 PM (19) to 8 AM (8) IST
                    if (hour >= 8 && hour < 19) {
                      return 'Day';
                    }
                    return 'Night';
                  } catch {
                    return 'Day';
                  }
                };

                // Calculate day and night shift stop times
                const calculateShiftStopTimes = () => {
                  let dayStopMs = 0;
                  let nightStopMs = 0;

                  stopEvents.forEach((event: any) => {
                    if (event.durationMs) {
                      const shift = getShiftForTimestamp(event.stoppedAt);
                      if (shift === 'Day') {
                        dayStopMs += event.durationMs;
                      } else {
                        nightStopMs += event.durationMs;
                      }
                    }
                  });

                  const formatDuration = (ms: number) => {
                    if (!ms || ms < 0) return '0s';
                    const seconds = Math.floor(ms / 1000);
                    const minutes = Math.floor(seconds / 60);
                    const hours = Math.floor(minutes / 60);
                    const remainingMinutes = minutes % 60;
                    const remainingSeconds = seconds % 60;

                    if (hours > 0) {
                      return `${hours}h ${remainingMinutes}m`;
                    } else if (minutes > 0) {
                      return `${minutes}m ${remainingSeconds}s`;
                    } else {
                      return `${remainingSeconds}s`;
                    }
                  };

                  return {
                    dayStopTime: formatDuration(dayStopMs),
                    nightStopTime: formatDuration(nightStopMs),
                    dayStopMs,
                    nightStopMs
                  };
                };

                const shiftTimes = calculateShiftStopTimes();

                return (
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Time Range:</span>
                      <span>{stopEventsData.from} - {stopEventsData.to}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Total Stops:</span>
                      <span className="font-semibold text-gray-900">{stopEventsData.stopCount || 0}</span>
                    </div>
                    {stopEventsData.totalStopTimeFormatted && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Total Stop Time:</span>
                        <span className="font-semibold text-red-600">{stopEventsData.totalStopTimeFormatted}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Day Shift Stop Time:</span>
                      <span className="font-semibold text-green-600">{shiftTimes.dayStopTime}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Night Shift Stop Time:</span>
                      <span className="font-semibold text-primary-600">{shiftTimes.nightStopTime}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {loadingStopEventsMachineId === selectedMachineForStopEvents.id ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading stop events...</div>
                </div>
              ) : stopEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No stop events recorded {stopEventsData ? `from ${stopEventsData.from} to ${stopEventsData.to}` : 'in this time period'}.
                </div>
              ) : (() => {
                // Helper function to determine shift for a timestamp (IST)
                const getShiftForTimestamp = (timestamp: string): 'Day' | 'Night' => {
                  if (!timestamp) return 'Day';
                  try {
                    // Parse timestamp in IST timezone
                    const date = dayjs.tz(timestamp, 'Asia/Kolkata');
                    const hour = date.hour();
                    // Day shift: 8 AM (8) to 7 PM (19) IST
                    // Night shift: 7 PM (19) to 8 AM (8) IST
                    if (hour >= 8 && hour < 19) {
                      return 'Day';
                    }
                    return 'Night';
                  } catch {
                    return 'Day';
                  }
                };

                // Format ISO-like timestamp (already in IST) to 12-hour time and remove T/Z
                const getFullDateTime = (isoString: string) => {
                  if (!isoString) return 'N/A';
                  try {
                    const [datePart, timePartRaw] = isoString.split('T');
                    if (!timePartRaw) {
                      return isoString;
                    }

                    const cleanedTime = timePartRaw.replace('Z', '').split('.')[0];
                    const [hourStr, minuteStr = '00', secondStr = '00'] = cleanedTime.split(':');

                    let hourNum = parseInt(hourStr, 10);
                    if (isNaN(hourNum)) {
                      return isoString.replace('T', ' ').replace('Z', '');
                    }

                    const period = hourNum >= 12 ? 'PM' : 'AM';
                    if (hourNum === 0) {
                      hourNum = 12;
                    } else if (hourNum > 12) {
                      hourNum -= 12;
                    }

                    const hour12 = hourNum.toString().padStart(2, '0');
                    const formattedTime = `${hour12}:${minuteStr}:${secondStr} ${period}`;
                    return `${datePart} ${formattedTime}`;
                  } catch {
                    return isoString.replace('T', ' ').replace('Z', '');
                  }
                };

                // Format duration helper
                const formatDuration = (ms: number) => {
                  if (!ms || ms < 0) return '0s';
                  const seconds = Math.floor(ms / 1000);
                  const minutes = Math.floor(seconds / 60);
                  const hours = Math.floor(minutes / 60);
                  const remainingMinutes = minutes % 60;
                  const remainingSeconds = seconds % 60;

                  if (hours > 0) {
                    return `${hours}h ${remainingMinutes}m`;
                  } else if (minutes > 0) {
                    return `${minutes}m ${remainingSeconds}s`;
                  } else {
                    return `${remainingSeconds}s`;
                  }
                };

                // Group stops by shift
                const dayStops = stopEvents.filter((event: any) => getShiftForTimestamp(event.stoppedAt) === 'Day');
                const nightStops = stopEvents.filter((event: any) => getShiftForTimestamp(event.stoppedAt) === 'Night');

                // Calculate totals for each shift
                const calculateShiftTotal = (stops: any[]) => {
                  const totalMs = stops.reduce((sum, event) => sum + (event.durationMs || 0), 0);
                  return {
                    count: stops.length,
                    totalMs,
                    totalFormatted: formatDuration(totalMs)
                  };
                };

                const dayTotal = calculateShiftTotal(dayStops);
                const nightTotal = calculateShiftTotal(nightStops);

                // Render stop table
                const reasonLabelMap: Record<string, string> = {};
                STOP_REASONS.forEach(r => { reasonLabelMap[r.value] = r.label; });

                const renderStopTable = (stops: any[], startIndex: number) => (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stopped At
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Resumed At
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duration
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Note
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stops.map((event, idx) => {
                        const isLongStop = event.durationMs && event.durationMs > (30 * 60 * 1000);
                        const noteKey = `${startIndex}-${idx}`;
                        const isEditing = editingNoteIdx === noteKey;
                        const hasNote = event.stopNote && event.stopNote.reason;
                        return (
                          <tr
                            key={idx}
                            className={`hover:bg-gray-50 ${isLongStop ? 'bg-red-50 border-l-4 border-red-500' : ''}`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {startIndex + idx + 1}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {getFullDateTime(event.stoppedAt)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {event.resumedAt
                                ? getFullDateTime(event.resumedAt)
                                : <span className="text-orange-600 font-medium">Ongoing</span>}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${isLongStop ? 'text-red-600' : 'text-gray-900'}`}>
                              {event.durationFormatted || 'N/A'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {isEditing ? (
                                <div className="flex flex-col gap-2 min-w-[220px]">
                                  <select
                                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                                    value={noteForm.reason}
                                    onChange={e => setNoteForm(prev => ({ ...prev, reason: e.target.value }))}
                                  >
                                    <option value="">Select reason...</option>
                                    {STOP_REASONS.map(r => (
                                      <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                  </select>
                                  <textarea
                                    className="border border-gray-300 rounded px-2 py-1 text-sm resize-none"
                                    rows={2}
                                    placeholder="Optional note..."
                                    value={noteForm.note}
                                    onChange={e => setNoteForm(prev => ({ ...prev, note: e.target.value }))}
                                  />
                                  <div className="flex gap-1">
                                    <button
                                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                      disabled={!noteForm.reason || savingNote}
                                      onClick={() => selectedMachineForStopEvents && handleSaveStopNote(
                                        selectedMachineForStopEvents.id,
                                        event.stoppedAt,
                                        noteForm.reason,
                                        noteForm.note
                                      )}
                                    >
                                      {savingNote ? 'Saving...' : 'Save'}
                                    </button>
                                    <button
                                      className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                      onClick={() => { setEditingNoteIdx(null); setNoteForm({ reason: '', note: '' }); }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : hasNote ? (
                                <div
                                  className="cursor-pointer group"
                                  onClick={() => {
                                    setEditingNoteIdx(noteKey);
                                    setNoteForm({
                                      reason: event.stopNote.reason,
                                      note: event.stopNote.note || ''
                                    });
                                  }}
                                >
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {reasonLabelMap[event.stopNote.reason] || event.stopNote.reason}
                                  </span>
                                  {event.stopNote.note && (
                                    <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]" title={event.stopNote.note}>
                                      {event.stopNote.note}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <button
                                  className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded p-1 transition-colors"
                                  title="Add note"
                                  onClick={() => {
                                    setEditingNoteIdx(noteKey);
                                    setNoteForm({ reason: '', note: '' });
                                  }}
                                >
                                  <Plus size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );

                return (
                  <div className="space-y-6">
                    {/* Day Shift Section */}
                    <div className="border border-green-200 rounded-lg overflow-hidden">
                      <div className="bg-green-50 px-4 py-3 border-b border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                              Day Shift
                            </span>
                            <span className="text-sm text-gray-600">
                              {dayTotal.count} stop{dayTotal.count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">Total Stop Time:</span>
                            <span className="text-lg font-bold text-green-600">{dayTotal.totalFormatted}</span>
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        {dayStops.length > 0 ? (
                          renderStopTable(dayStops, 0)
                        ) : (
                          <div className="px-4 py-8 text-center text-gray-500 text-sm">
                            No stop events during Day Shift
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Night Shift Section */}
                    <div className="border border-primary-200 rounded-lg overflow-hidden">
                      <div className="bg-primary-50 px-4 py-3 border-b border-primary-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-primary-100 text-primary-800">
                              Night Shift
                            </span>
                            <span className="text-sm text-gray-600">
                              {nightTotal.count} stop{nightTotal.count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">Total Stop Time:</span>
                            <span className="text-lg font-bold text-primary-600">{nightTotal.totalFormatted}</span>
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        {nightStops.length > 0 ? (
                          renderStopTable(nightStops, dayStops.length)
                        ) : (
                          <div className="px-4 py-8 text-center text-gray-500 text-sm">
                            No stop events during Night Shift
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Export All Machines Modal */}
      {showExportAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1004]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Export All Machines Report
              </h3>
              <button
                onClick={() => {
                  setShowExportAllModal(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">


              {/* Preset Selection Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time Range
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setExportAllPreset('today')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportAllPreset === 'today'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportAllPreset('yesterday')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportAllPreset === 'yesterday'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Yesterday
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportAllPreset('custom')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportAllPreset === 'custom'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Custom
                  </button>
                </div>
              </div>

              {/* Custom Date Range */}
              {exportAllPreset === 'custom' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={exportAllStartDate}
                        onChange={(e) => {
                          setExportAllStartDate(e.target.value);
                          // Limit to last 2 months
                          if (e.target.value) {
                            try {
                              const selectedDate = dayjs.tz(e.target.value, 'YYYY-MM-DD', 'Asia/Kolkata');
                              if (!selectedDate.isValid()) {
                                return;
                              }
                              const twoMonthsAgo = dayjs().tz('Asia/Kolkata').subtract(2, 'month');
                              if (selectedDate.isBefore(twoMonthsAgo)) {
                                toast.error('Start date cannot be more than 2 months ago');
                                setExportAllStartDate(twoMonthsAgo.format('YYYY-MM-DD'));
                              }
                            } catch (error) {
                              console.error('Date validation error:', error);
                            }
                          }
                        }}
                        max={dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD')}
                        min={dayjs().tz('Asia/Kolkata').subtract(2, 'month').format('YYYY-MM-DD')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={exportAllEndDate}
                        onChange={(e) => {
                          setExportAllEndDate(e.target.value);
                          // Validate against start date
                          if (exportAllStartDate && e.target.value) {
                            try {
                              const start = dayjs.tz(exportAllStartDate, 'YYYY-MM-DD', 'Asia/Kolkata');
                              const end = dayjs.tz(e.target.value, 'YYYY-MM-DD', 'Asia/Kolkata');
                              if (!start.isValid() || !end.isValid()) {
                                return;
                              }
                              if (end.isBefore(start)) {
                                toast.error('End date must be after start date');
                              }
                              const diffMonths = end.diff(start, 'month', true);
                              if (diffMonths > 2) {
                                toast.error('Date range cannot exceed 2 months');
                              }
                            } catch (error) {
                              console.error('Date validation error:', error);
                            }
                          }
                        }}
                        max={dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD')}
                        min={exportAllStartDate || dayjs().tz('Asia/Kolkata').subtract(2, 'month').format('YYYY-MM-DD')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-gray-500">
                    Maximum date range: 2 months (past 2 months allowed)
                  </div>
                </>
              )}

              {/* Shift Selection — Yesterday and Custom range */}
              {(exportAllPreset === 'yesterday' || exportAllPreset === 'custom') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Shift
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setExportAllShift('both')}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportAllShift === 'both'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                      >
                        {exportAllPreset === 'custom' ? 'Full Day' : 'Both Shifts'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setExportAllShift('day')}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportAllShift === 'day'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                      >
                        Day Shift
                      </button>
                      <button
                        type="button"
                        onClick={() => setExportAllShift('night')}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportAllShift === 'night'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                      >
                        Night Shift
                      </button>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    {exportAllPreset === 'custom' ? (
                      <div className="text-sm text-blue-800">
                        {exportAllShift === 'both' && (
                          <span>📅 <strong>Full Day:</strong> From start date 8 AM IST to the day after end date 8 AM IST (or now)</span>
                        )}
                        {exportAllShift === 'day' && (
                          <span>☀️ <strong>Day Shift:</strong> Only 8 AM to 7 PM IST of each day in the range</span>
                        )}
                        {exportAllShift === 'night' && (
                          <span>🌙 <strong>Night Shift:</strong> Only 7 PM to next day 8 AM IST of each day in the range</span>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-blue-800">
                        {exportAllShift === 'both' && (
                          <span>📅 <strong>Both Shifts:</strong> From yesterday 8 AM IST to today 8 AM IST</span>
                        )}
                        {exportAllShift === 'day' && (
                          <span>☀️ <strong>Day Shift:</strong> From yesterday 8 AM IST to yesterday 7 PM IST</span>
                        )}
                        {exportAllShift === 'night' && (
                          <span>🌙 <strong>Night Shift:</strong> From yesterday 7 PM IST to today 8 AM IST</span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Preset Info for Today */}
              {exportAllPreset === 'today' && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                  <div className="text-sm text-purple-800">
                    <span>📅 <strong>Today:</strong> From today 8 AM IST to now</span>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowExportAllModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isExportingAll}
              >
                Cancel
              </button>
              <button
                onClick={() => handleExportAllMachinesPDF('print')}
                disabled={isExportingAll}
                className="px-4 py-2 text-sm font-medium text-purple-700 bg-white border border-purple-600 rounded-md hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Printer className="h-4 w-4" />
                <span>Print</span>
              </button>
              <button
                onClick={() => handleExportAllMachinesPDF('download')}
                disabled={isExportingAll}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isExportingAll ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Export PDF</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Report Modal */}
      {showExportModal && selectedMachineForExport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1004]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Export Report - Machine #{selectedMachineForExport?.number}
              </h3>
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setSelectedMachineForExport(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">

              {/* Preset Selection Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setExportPreset('today')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportPreset === 'today'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportPreset('yesterday')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportPreset === 'yesterday'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Yesterday
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportPreset('last3days')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportPreset === 'last3days'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Last 3 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportPreset('custom')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportPreset === 'custom'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Custom
                  </button>
                </div>
              </div>

              {/* Shift Selection — full range vs day (8 AM–7 PM) / night (7 PM–8 AM) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Shift
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setExportShift('both')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportShift === 'both'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Full Range
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportShift('day')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportShift === 'day'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Day Shift
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportShift('night')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${exportShift === 'night'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Night Shift
                  </button>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {exportShift === 'both' && 'Full selected range (both shifts).'}
                  {exportShift === 'day' && 'Only 8 AM – 7 PM IST of each day in the range.'}
                  {exportShift === 'night' && 'Only 7 PM – 8 AM IST of each day in the range.'}
                </div>
              </div>

              {/* Custom Date Range (only show when custom is selected) */}
              {exportPreset === 'custom' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={exportStartDate}
                        onChange={(e) => {
                          setExportStartDate(e.target.value);
                          // Limit to last 2 months
                          if (e.target.value) {
                            try {
                              const selectedDate = dayjs.tz(e.target.value, 'YYYY-MM-DD', 'Asia/Kolkata');
                              if (!selectedDate.isValid()) {
                                return;
                              }
                              const twoMonthsAgo = dayjs().tz('Asia/Kolkata').subtract(2, 'month');
                              if (selectedDate.isBefore(twoMonthsAgo)) {
                                toast.error('Start date cannot be more than 2 months ago');
                                setExportStartDate(twoMonthsAgo.format('YYYY-MM-DD'));
                              }
                            } catch (error) {
                              console.error('Date validation error:', error);
                            }
                          }
                        }}
                        max={dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD')}
                        min={dayjs().tz('Asia/Kolkata').subtract(2, 'month').format('YYYY-MM-DD')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time (IST)
                      </label>
                      <input
                        type="time"
                        value={exportStartTime}
                        onChange={(e) => setExportStartTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={exportEndDate}
                        onChange={(e) => {
                          setExportEndDate(e.target.value);
                          // Validate against start date
                          if (exportStartDate && e.target.value && exportStartTime && exportEndTime) {
                            try {
                              const start = dayjs.tz(`${exportStartDate} ${exportStartTime}`, 'YYYY-MM-DD HH:mm', 'Asia/Kolkata');
                              const end = dayjs.tz(`${e.target.value} ${exportEndTime}`, 'YYYY-MM-DD HH:mm', 'Asia/Kolkata');
                              if (!start.isValid() || !end.isValid()) {
                                return;
                              }
                              if (end.isBefore(start)) {
                                toast.error('End date must be after start date');
                              }
                              const diffMonths = end.diff(start, 'month', true);
                              if (diffMonths > 2) {
                                toast.error('Date range cannot exceed 2 months');
                              }
                            } catch (error) {
                              console.error('Date validation error:', error);
                            }
                          }
                        }}
                        max={dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD')}
                        min={exportStartDate || dayjs().tz('Asia/Kolkata').subtract(2, 'month').format('YYYY-MM-DD')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time (IST)
                      </label>
                      <input
                        type="time"
                        value={exportEndTime}
                        onChange={(e) => setExportEndTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-gray-500">
                    Maximum date range: 2 months
                  </div>
                </>
              )}

              {/* Preset Info */}
              {exportPreset !== 'custom' && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                  <div className="text-sm text-purple-800">
                    {exportPreset === 'today' && (
                      <span>📅 <strong>Today:</strong> From today 8 AM IST to now</span>
                    )}
                    {exportPreset === 'yesterday' && (
                      <span>📅 <strong>Yesterday:</strong> From yesterday 8 AM IST to today 8 AM IST</span>
                    )}
                    {exportPreset === 'last3days' && (
                      <span>📅 <strong>Last 3 Days:</strong> From 3 days ago 8 AM IST to now</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setSelectedMachineForExport(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleExportPDF('print')}
                disabled={isExporting}
                className="px-4 py-2 text-sm font-medium text-purple-700 bg-white border border-purple-600 rounded-md hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Printer className="h-4 w-4" />
                <span>Print</span>
              </button>
              <button
                onClick={() => handleExportPDF('download')}
                disabled={isExporting}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Export PDF</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Category Modal — lists the machines counted by a status chip */}
      {selectedStatusCategory && (() => {
        const meta = STATUS_CATEGORY_META[selectedStatusCategory];
        const machinesInCategory = [...statusGroups[selectedStatusCategory]]
          .sort((a, b) => (parseInt(a.number) || 0) - (parseInt(b.number) || 0));
        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1003] p-4"
            onClick={() => setSelectedStatusCategory(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className={`text-lg font-bold ${meta.title}`}>
                  {meta.label} — {machinesInCategory.length} {machinesInCategory.length === 1 ? 'Machine' : 'Machines'}
                </h3>
                <button
                  onClick={() => setSelectedStatusCategory(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="overflow-y-auto p-4">
                {machinesInCategory.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">No machines in this category.</p>
                ) : (
                  <>
                    <p className="text-xs text-gray-400 mb-3">Tap a machine to open its details.</p>
                    <div className="flex flex-wrap gap-2">
                      {machinesInCategory.map((machine) => (
                        <button
                          key={machine.id}
                          type="button"
                          onClick={() => {
                            setSelectedStatusCategory(null);
                            setSelectedMachineForCardModal(machine);
                            setShowMachineCardModal(true);
                          }}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-bold font-mono transition cursor-pointer ${meta.badge}`}
                        >
                          #{machine.number}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Machine Card Modal
          z-[1003]: sits BELOW every action modal the card can open (export, settings,
          pick/efficiency edit, beam, delete = z-[1004]/z-[1010]) so they open on top.
          Same z as the status-category modal but rendered after it, so a card opened
          from that list still shows above the list. */}
      {showMachineCardModal && selectedMachineForCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1003] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                Machine #{selectedMachineForCardModal.number} - Details
              </h3>
              <button
                onClick={() => {
                  setShowMachineCardModal(false);
                  setSelectedMachineForCardModal(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              <div className="flex justify-center">
                <div className="w-full">
                  <MachineCard
                    key={selectedMachineForCardModal.id}
                    machine={selectedMachineForCardModal}
                    isRowLoading={fetchingMachineIds.has(selectedMachineForCardModal.id)}
                    isRowApiLoading={loadingStopEventsMachineId === selectedMachineForCardModal.id}
                    handleMachineSettings={handleMachineSettings}
                    handleExportReport={handleExportReport}
                    handlePickEdit={handlePickEdit}
                    handleEfficiencyEdit={handleEfficiencyEdit}
                    setDeletingMachine={setDeletingMachine}
                    getMachineWithStatus={getMachineWithStatus}
                    shouldHighlightMachine={shouldHighlightMachine}
                    getMachineStatus={getMachineStatus}
                    bgColor={getBgColor(selectedMachineForCardModal as Machine)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── TV Mode helper components ─────────────────────────────────────────

const toRgbaChip = (c: string, a: number): string => {
  if (!c) return `rgba(100,116,139,${a})`;
  const s = c.trim();
  if (s.startsWith('rgba(')) return s;
  if (s.startsWith('rgb(')) return s.replace('rgb(', 'rgba(').replace(')', `,${a})`);
  if (s.startsWith('#')) {
    const h = s.slice(1);
    const n = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
    const r = parseInt(n.slice(0, 2), 16);
    const g = parseInt(n.slice(2, 4), 16);
    const b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  return s;
};

const toneHexMap: Record<string, string> = {
  emerald: '#059669',
  green:   '#16a34a',
  red:     '#dc2626',
  blue:    '#0060b9',
  cyan:    '#0891b2',
  indigo:  '#4338ca',
  fuchsia: '#c026d3',
  yellow:  '#ca8a04',
  orange:  '#ea580c',
};

// Chip sized & spaced entirely by CSS tokens (.tv-kpi* classes in index.css).
// A `color` prop overrides the tone palette with the DB-driven efficiency/status color.
const KpiChip: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; tone: string; color?: string }> = ({ icon, label, value, tone, color }) => {
  const resolved = color || toneHexMap[tone] || toneHexMap.emerald;
  return (
    <div
      className="tv-kpi"
      style={{
        background: toRgbaChip(resolved, 0.08),
        borderColor: toRgbaChip(resolved, 0.28),
      }}
    >
      <span className="tv-kpi__icon" style={{ color: resolved }}>{icon}</span>
      <div className="tv-kpi__body">
        <span className="tv-kpi__label" style={{ color: toRgbaChip(resolved, 0.95) }}>{label}</span>
        <span className="tv-kpi__value" style={{ color: resolved }}>{value}</span>
      </div>
    </div>
  );
};

const TvClock: React.FC = () => {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="text-[11px] text-slate-600 font-mono font-semibold tabular-nums bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
      {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
    </span>
  );
};

export default Machines;
